import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Select,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Autocomplete,
  Drawer,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  RadioGroup,
  Radio,
  Tooltip
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { DataGridPro } from '@mui/x-data-grid-pro';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import MapIcon from '@mui/icons-material/Map';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CommentIcon from '@mui/icons-material/Comment';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import EditIcon from '@mui/icons-material/Edit';
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import { bankAccountNumberForApiQuery } from './cashReconPropertyApi';
import AdjustmentPopup from './AdjustmentPopup';
import BankDrawer from './BankDrawer';
import ChartOfAccountsDrawer from './ChartOfAccountsDrawer';
import { appendCashReconLedgerCoaQueryParams, unwrapGlTransactionPage } from './cashReconLedgerScope';
import GLTransactionDrawer from './GLTransactionDrawer';
import BankStatementDrawer from './BankStatement';
import GLUpload from './GLUpload';
import BankUpload from './BankUpload';
import UnifiedUpload from './UnifiedUpload';
import GLReference from './GLReference';
import BankReference from './BankReference';
import RefreshData from './RefreshData';
import Reclassification from './Reclassification';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateProceedButtonSx,
  harmonateUploadOutlinedSx,
  harmonateCancelButtonSx,
  harmonateDataGridSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogFormContentSx,
  harmonateFormFieldsSx,
  harmonateAbortButtonSx,
  harmonateToolbarBuFieldSx,
  harmonateToolbarButtonSx,
  harmonatePlainIconButtonSx,
  harmonateDrawerHeaderFlushSx,
  harmonateDrawerBodySx,
  harmonateDialogCloseIconSx,
  harmonateDrawerAccordionTitleSx,
  harmonateSelectMenuProps,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const Mapping = () => {
  const { canAdd, canEdit, canDelete } = useFunctionPermissions(FC.CASH_RECONCILIATION);
  const navigate = useNavigate();
  const location = useLocation();
  const dashboardNavApplied = useRef(false);
  const pendingNavMonthRef = useRef('');
  const { toastState, showSuccess, showError, showInfo, hideToast, showToast } = useToast();
  const [reconGateMessage, setReconGateMessage] = useState('');
  const [reconGateStatus, setReconGateStatus] = useState('');

  // Menu state for 3-dot menu
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const menuOpen = Boolean(menuAnchorEl);

  // State for GL grid
  const [glRows, setGlRows] = useState([]);
  const [glLoading, setGlLoading] = useState(false);
  const [glError, setGlError] = useState(null);
  const [glDataLoaded, setGlDataLoaded] = useState(false);
  const [glPaginationModel, setGlPaginationModel] = useState({ page: 0, pageSize: 100 });
  const [glRowCount, setGlRowCount] = useState(0);
  const glFetchSeq = useRef(0);

  // State for GL side drawer
  const [glDrawerOpen, setGlDrawerOpen] = useState(false);
  const [selectedGlRow, setSelectedGlRow] = useState(null);
  const [glComment, setGlComment] = useState('');

  // State for row action menu
  const [rowMenuAnchorEl, setRowMenuAnchorEl] = useState(null);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const rowMenuOpen = Boolean(rowMenuAnchorEl);

  // State for Approve confirmation dialog
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [editableEndingBalance, setEditableEndingBalance] = useState(0);
  const [approving, setApproving] = useState(false);
  const [approvalScope, setApprovalScope] = useState('selected_period'); // 'selected_period' or 'mapped_records'

  // State for Auto Matching dialog
  const [autoMatchingDialogOpen, setAutoMatchingDialogOpen] = useState(false);
  const [autoMatchedPairs, setAutoMatchedPairs] = useState([]);
  const [autoMatchingLoading, setAutoMatchingLoading] = useState(false);

  // State for Bank grid
  const [bankRows, setBankRows] = useState([]);
  const [bankLoading, setBankLoading] = useState(false);
  const [bankError, setBankError] = useState(null);
  const [bankPaginationModel, setBankPaginationModel] = useState({ page: 0, pageSize: 100 });
  const [bankRowCount, setBankRowCount] = useState(0);

  // Mapping dialog state
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [selectedSourceRow, setSelectedSourceRow] = useState(null);
  const [isMappingFromGL, setIsMappingFromGL] = useState(true); // true if mapping from GL, false if from Bank
  const [selectedTargetRows, setSelectedTargetRows] = useState([]); // Selected rows in the target grid
  const [targetGridSearch, setTargetGridSearch] = useState('');
  const [targetGridRows, setTargetGridRows] = useState([]);
  const [targetGridAllRows, setTargetGridAllRows] = useState([]); // Store all unfiltered data
  const [targetGridLoading, setTargetGridLoading] = useState(false);
  const [targetGridPaginationModel, setTargetGridPaginationModel] = useState({ page: 0, pageSize: 100 });
  const [targetGridRowCount, setTargetGridRowCount] = useState(0);
  const [savingMapping, setSavingMapping] = useState(false);
  const [existingMappings, setExistingMappings] = useState([]); // Store full mapping objects with IDs
  const [enableDeleteMode, setEnableDeleteMode] = useState(false); // Enable delete mode in mapping dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [mappingToDelete, setMappingToDelete] = useState(null); // Mapping object to delete
  const [deletingMapping, setDeletingMapping] = useState(false);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false); // Delete all mappings confirmation dialog
  const [deletingAllMappings, setDeletingAllMappings] = useState(false);

  // Reclassify confirmation dialog state
  const [reclassifyDialogOpen, setReclassifyDialogOpen] = useState(false);
  const [glTransactionToReclassify, setGlTransactionToReclassify] = useState(null);
  const [reclassifyingTransaction, setReclassifyingTransaction] = useState(false);
  
  // Bulk reclassify state
  const [selectedGlRows, setSelectedGlRows] = useState([]);
  const [bulkReclassifyDialogOpen, setBulkReclassifyDialogOpen] = useState(false);
  const [reclassifyingBulk, setReclassifyingBulk] = useState(false);

  // Push to another date dialog state
  const [pushToDateDialogOpen, setPushToDateDialogOpen] = useState(false);
  const [glRecordForPush, setGlRecordForPush] = useState(null);
  const [pushDate, setPushDate] = useState('');
  const [pushing, setPushing] = useState(false);

  // Balance state
  const [balanceData, setBalanceData] = useState({
    glBalance: 0,
    bankBalance: 0,
    delta: 0,
    endingBankBalance: 0,
    glDebits: 0,
    glCredits: 0,
    bankDebits: 0,
    bankCredits: 0,
    totalMappings: 0,
  });
  const [balanceLoading, setBalanceLoading] = useState(false);

  // Header state for cash recon mapping
  const [headerId, setHeaderId] = useState(null);
  const [mappingHeaders, setMappingHeaders] = useState([]);
  const [mappingHeadersLoading, setMappingHeadersLoading] = useState(false);

  // Unified property selection for both GL and Bank sections
  const [selectedProperty, setSelectedProperty] = useState('');

  // Bank filters - month year only (property removed)
  const [selectedBankMonthYear, setSelectedBankMonthYear] = useState('');
  const [bankMonthYearOptions, setBankMonthYearOptions] = useState([]);
  const [bankMonthYearLoading, setBankMonthYearLoading] = useState(false);
  /** Full rows from cash-recon-mapping-headers/by-property (for bankAccountNo by month) */
  const [bankPropertyMappingHeaders, setBankPropertyMappingHeaders] = useState([]);
  /** Property go-live (YYYY-MM); months before this are Historical / view-only on Mapping */
  const [propertyGoLiveMonth, setPropertyGoLiveMonth] = useState('');

  // Bank dropdown state
  const [selectedBank, setSelectedBank] = useState('');
  const [banks, setBanks] = useState([]);
  const [banksLoading, setBanksLoading] = useState(false);

  const [filters, setFilters] = useState({
    quarter: '',
    drCr: '',
    search: '',
  });

  // Separate filter states for Bank and GL grids
  const [bankFilters, setBankFilters] = useState({
    monthYear: '', // Format: YYYY-MM
    state: 'all',
  });

  const [glFilters, setGlFilters] = useState({
    fromDate: '', // Format: YYYY-MM-DD
    toDate: '', // Format: YYYY-MM-DD
    state: 'all',
  });

  const apiMappingState = (uiState) =>
    uiState === 'partially_mapped' ? 'all' : uiState;

  // GL grid buckets. Output and the map-target picker still use apiMappingState
  // ("mapped" = any mapping row). The grid's Fully / Partially labels follow amount rules.
  const glMappingApiState = (uiState) => {
    if (uiState === 'mapped') return 'fully_mapped';
    if (uiState === 'partially_mapped') return 'partially_mapped';
    if (uiState === 'unmapped') return 'unmapped';
    return 'all';
  };

  const filterRowsByUiState = (rows, uiState) => {
    if (uiState === 'partially_mapped') {
      return rows.filter((r) => r.mappingStatus === 'partially_mapped');
    }
    if (uiState === 'mapped') {
      return rows.filter((r) => r.mappingStatus === 'fully_mapped');
    }
    return rows;
  };

  // State for properties list
  const [properties, setProperties] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);

  const [adjustmentDialogOpen, setAdjustmentDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState(null);

  // Bank drawer state
  const [bankDrawerOpen, setBankDrawerOpen] = useState(false);

  // Chart of Accounts drawer state
  const [chartOfAccountsDrawerOpen, setChartOfAccountsDrawerOpen] = useState(false);

  // GL Transaction drawer state
  const [glTransactionDrawerOpen, setGlTransactionDrawerOpen] = useState(false);

  // Bank Statement drawer state
  const [bankStatementDrawerOpen, setBankStatementDrawerOpen] = useState(false);

  // Refresh Data dialog state
  const [refreshDataOpen, setRefreshDataOpen] = useState(false);

  // GL Upload dialog state
  const [glUploadDialogOpen, setGlUploadDialogOpen] = useState(false);

  // Bank Upload dialog state
  const [bankUploadDialogOpen, setBankUploadDialogOpen] = useState(false);

  // Unified Upload dialog state
  const [unifiedUploadDialogOpen, setUnifiedUploadDialogOpen] = useState(false);

  // GL Reference page state
  const [showGLReference, setShowGLReference] = useState(false);

  // Bank Reference page state
  const [showBankReference, setShowBankReference] = useState(false);

  // Reclassification page state
  const [showReclassification, setShowReclassification] = useState(false);

  const handleAddAdjustment = (adjustmentData) => {
    // Handle the adjustment data here
    console.log('Adjustment data:', adjustmentData);
    // Add your API call or state update logic here

    // Refresh GL grid after adjustment is added
    fetchGLData(glPaginationModel.page, glPaginationModel.pageSize);
  };

  const handleRowMenuClick = (event, rowData) => {
    setRowMenuAnchorEl(event.currentTarget);
    setSelectedRowData(rowData);
  };

  const handleRowMenuClose = () => {
    setRowMenuAnchorEl(null);
    setSelectedRowData(null);
  };

  const handleEditAdjustment = (rowData) => {
    setEditMode(true);
    setEditData(rowData);
    setAdjustmentDialogOpen(true);
  };

  const handleAdjustmentClose = () => {
    setAdjustmentDialogOpen(false);
    setEditMode(false);
    setEditData(null);
  };

  const handleOpenGLReference = () => {
    setShowGLReference(true);
    setMenuAnchorEl(null);
  };

  const handleOpenBankReference = () => {
    setShowBankReference(true);
    setMenuAnchorEl(null);
  };

  const handleOpenReclassification = () => {
    setShowReclassification(true);
    setMenuAnchorEl(null);
  };

  const sumMappedBankAmounts = (arr) =>
    Array.isArray(arr) ? arr.reduce((acc, v) => acc + (Number(v) || 0), 0) : 0;

  const mapGlApiItem = (item) => {
    let mappingStatus = item.mappedStatus?.toLowerCase() || 'unmapped';
    const debitAmount = item.debitAmount || 0;
    const creditAmount = item.creditAmount || 0;
    const apiMappedAmount = item.mappedAmount || 0;
    const bankAmountsSum = sumMappedBankAmounts(item.mappedBankAmounts);
    const mappedAmount =
      item.mappedBankIds?.length > 0
        ? Math.max(apiMappedAmount, bankAmountsSum)
        : apiMappedAmount;

    if (mappedAmount && (debitAmount || creditAmount)) {
      const totalAmount = Math.abs(debitAmount || creditAmount);
      const delta = Math.abs(totalAmount - mappedAmount);
      const amountTol = 0.01;
      const overMapped = mappedAmount > totalAmount + amountTol;

      if (overMapped) {
        mappingStatus = 'partially_mapped';
      } else if (item.mappedStatus === 'MAPPED' && item.mappedBankIds && item.mappedBankIds.length > 0) {
        mappingStatus = 'fully_mapped';
      } else if (delta < amountTol) {
        mappingStatus = 'fully_mapped';
      } else if (delta < totalAmount) {
        mappingStatus = 'partially_mapped';
      }
    }

    return {
      id: item.id,
      description: item.description,
      date: item.transactionDate,
      docNo: item.referenceNo,
      debit: debitAmount,
      credit: creditAmount,
      selected: false,
      isMapped: item.mappedStatus === 'MAPPED',
      mappingStatus,
      integrationStatus: item.integrationStatus || '',
      originalData: item,
    };
  };

  // Same header used for the GL Account label: mappingHeaders after Bank Load,
  // otherwise the by-property month/year list (available as soon as Month & Year is set).
  const resolveMappingHeaderForGl = () => {
    if (!selectedProperty || selectedProperty === 'All' || !selectedBankMonthYear) return null;
    const selectedProp = properties.find(p => p.PROPERTY_NAME === selectedProperty || p.propertyName === selectedProperty);
    const propertyId = selectedProp?.PROPERTY_ID || selectedProp?.propertyId || selectedProperty;
    const fromMappingList = mappingHeaders.find(h =>
      (h.propertyId === propertyId || h.propertyId === selectedProperty) &&
      h.monthYear === selectedBankMonthYear &&
      (!bankAccountNoForSelectedMonth || String(h?.bankAccountNo || '').trim() === String(bankAccountNoForSelectedMonth).trim())
    ) || mappingHeaders.find(h =>
      (h.propertyId === propertyId || h.propertyId === selectedProperty) &&
      h.monthYear === selectedBankMonthYear
    );
    const fromPropertyHeaders = bankPropertyMappingHeaders.find((h) =>
      h.monthYear === selectedBankMonthYear &&
      (!bankAccountNoForSelectedMonth || String(h?.bankAccountNo || '').trim() === String(bankAccountNoForSelectedMonth).trim())
    ) || bankPropertyMappingHeaders.find((h) => h.monthYear === selectedBankMonthYear);
    const hasGlInfo = (h) =>
      h && (String(h.glAccountName || '').trim() !== '' || String(h.glAccountNo || '').trim() !== '');
    if (fromMappingList && hasGlInfo(fromMappingList)) return fromMappingList;
    if (fromPropertyHeaders && hasGlInfo(fromPropertyHeaders)) return fromPropertyHeaders;
    return fromMappingList || fromPropertyHeaders || null;
  };

  // stateToken is the API state (fully_mapped, mapped, all, ...), not the UI label.
  const buildGlMappingFilterUrl = (tenantId, { stateToken, limit, offset, search }) => {
    let apiUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/gl-transactions/by-tenant-mapping-filter?tenant_id=${tenantId}&state=${stateToken}`;
    if (limit != null) apiUrl += `&limit=${limit}`;
    if (offset != null) apiUrl += `&offset=${offset}`;
    if (search) apiUrl += `&search=${encodeURIComponent(search)}`;
    if (glFilters.fromDate) apiUrl += `&start_date=${glFilters.fromDate}`;
    if (glFilters.toDate) apiUrl += `&end_date=${glFilters.toDate}`;
    if (selectedProperty && selectedProperty !== 'All') {
      const selectedProp = properties.find(p => p.PROPERTY_NAME === selectedProperty || p.propertyName === selectedProperty);
      const propertyId = selectedProp?.PROPERTY_ID || selectedProp?.propertyId || selectedProperty;
      apiUrl += `&property_id=${propertyId}`;
      const glAccountNo = String(resolveMappingHeaderForGl()?.glAccountNo || '').trim();
      if (glAccountNo) {
        apiUrl += `&gl_account_no=${encodeURIComponent(glAccountNo)}`;
      }
    }
    return apiUrl;
  };

  const fetchGlMappingPage = async (token, tenantId, { stateToken, limit, offset, search }) => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';
    const apiUrl = await appendCashReconLedgerCoaQueryParams(
      buildGlMappingFilterUrl(tenantId, { stateToken, limit, offset, search }),
      { baseUrl, tenantId, token }
    );
    const resp = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    if (!resp.ok) {
      throw new Error(`Failed to fetch GL data: ${resp.status} ${resp.statusText}`);
    }
    const page = unwrapGlTransactionPage(await resp.json());
    if (!page) {
      throw new Error('Invalid response format from GL transactions API');
    }
    return page;
  };

  // Every page of the current GL filter. Used by approve and auto-match, which act on the
  // whole filtered set, not only the page on screen. Each request stays capped.
  const loadAllFilteredGlRows = async () => {
    const token = localStorage.getItem('token');
    const tenantId = localStorage.getItem('tenant_id');
    if (!token) throw new Error('No authentication token found');
    if (!tenantId) throw new Error('No tenant ID found');
    const chunkSize = 1000;
    let offset = 0;
    let total = Number.POSITIVE_INFINITY;
    const rows = [];
    let guard = 0;
    const stateToken = glMappingApiState(glFilters.state);
    while (offset < total && guard < 10000) {
      guard += 1;
      const page = await fetchGlMappingPage(token, tenantId, { stateToken, limit: chunkSize, offset });
      total = page.total;
      if (page.content.length === 0) break;
      rows.push(...page.content.map(mapGlApiItem));
      offset += page.content.length;
    }
    return rows;
  };

  // Fetch one page of GL data for the grid.
  const fetchGLData = async (page, pageSize) => {
    const seq = ++glFetchSeq.current;
    try {
      setGlLoading(true);
      setGlError(null);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token) {
        throw new Error('No authentication token found');
      }
      if (!tenantId) {
        throw new Error('No tenant ID found');
      }

      const safePage = Number.isFinite(page) ? page : 0;
      const safePageSize = Number.isFinite(pageSize) && pageSize > 0 ? pageSize : 100;
      const result = await fetchGlMappingPage(token, tenantId, {
        stateToken: glMappingApiState(glFilters.state),
        limit: safePageSize,
        offset: safePage * safePageSize,
      });

      if (seq !== glFetchSeq.current) return;
      setGlRows(result.content.map(mapGlApiItem));
      setGlRowCount(result.total);
      setGlDataLoaded(true);
    } catch (err) {
      if (seq !== glFetchSeq.current) return;
      console.error('Error fetching GL data:', err);
      setGlError(err.message || 'Failed to fetch GL data');
      setGlRows([]);
      setGlRowCount(0);
      showError(err.message || 'Failed to fetch GL data');
    } finally {
      if (seq === glFetchSeq.current) {
        setGlLoading(false);
        setGlDataLoaded(true);
      }
    }
  };

  // Fetch banks for dropdown
  const fetchBanks = async () => {
    try {
      setBanksLoading(true);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token || !tenantId) {
        throw new Error('Missing authentication or tenant information');
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/banks/by-tenant-and-status?tenant_id=${tenantId}&status=ACTIVE`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch banks: ${response.status}`);
      }

      const data = await response.json();
      setBanks(data || []);
    } catch (err) {
      console.error('Error fetching banks:', err);
      setBanks([]);
      showError('Failed to load banks');
    } finally {
      setBanksLoading(false);
    }
  };

  // Fetch Bank data from API
  const fetchBankData = async (page, pageSize) => {
    if (!selectedProperty || !selectedBankMonthYear) {
      showError('Please select Property and Month & Year to load Bank data');
      return;
    }

    try {
      setBankLoading(true);
      setBankError(null);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token) {
        throw new Error('No authentication token found');
      }
      if (!tenantId) {
        throw new Error('No tenant ID found');
      }

      // Convert month/year to date range
      const [year, month] = selectedBankMonthYear.split('-');
      const startDate = `${year}-${month}-01`;
      const lastDay = new Date(year, parseInt(month), 0).getDate(); // Get last day of month
      const endDate = `${year}-${month}-${lastDay.toString().padStart(2, '0')}`;

      // Get property ID from properties list
      const selectedProp = properties.find(p => p.PROPERTY_NAME === selectedProperty || p.propertyName === selectedProperty);
      const propertyId = selectedProp?.PROPERTY_ID || selectedProp?.propertyId || selectedProperty;

      // New API endpoint with mapping filter and state parameter
      let apiUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/bank-statements/by-tenant-mapping-filter?tenant_id=${tenantId}&start_date=${startDate}&end_date=${endDate}&property_id=${propertyId}&state=${apiMappingState(bankFilters.state)}`;
      if (bankAccountNoForSelectedMonth) {
        apiUrl += `&bank_account_number=${encodeURIComponent(bankAccountNumberForApiQuery(bankAccountNoForSelectedMonth))}`;
      }

      const resp = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) {
        throw new Error(`Failed to fetch bank data: ${resp.status} ${resp.statusText}`);
      }

      const data = await resp.json();

      if (Array.isArray(data)) {
        // Map the new API response to the expected format for the grid
        const mappedData = data.map(item => {
          // Use API's mappedStatus if available, otherwise calculate from reconStatus
          let mappingStatus = item.mappedStatus?.toLowerCase() || 'unmapped';

          // If mappedAmount exists, calculate delta for partial vs full mapping
          const debitAmount = item.debitAmount || 0;
          const creditAmount = item.creditAmount || 0;
          const mappedAmount = item.mappedAmount || 0;

          if (mappedAmount && (debitAmount || creditAmount)) {
            const totalAmount = Math.abs(debitAmount || creditAmount);
            const delta = Math.abs(totalAmount - mappedAmount);

            if (delta < 0.01) {
              mappingStatus = 'fully_mapped';
            } else if (delta < totalAmount) {
              mappingStatus = 'partially_mapped';
            }
          }

          return {
            id: item.id,
            description: item.description,
            date: item.transactionDate,
            docNo: item.referenceNo,
            debit: debitAmount,
            credit: creditAmount,
            remarks: item.notes || '',
            selected: false,
            isMapped: item.mappedStatus === 'MAPPED',
            mappingStatus: mappingStatus,
            // Keep original data for reference
            originalData: item,
          };
        });

        const bankRowsForGrid = filterRowsByUiState(mappedData, bankFilters.state);

        setBankRows(bankRowsForGrid);
        setBankRowCount(bankRowsForGrid.length);
      } else {
        throw new Error('Invalid response format from bank statements API');
      }
    } catch (err) {
      console.error('Error fetching bank data:', err);
      setBankError(err.message || 'Failed to fetch bank data');
      setBankRows([]);
      setBankRowCount(0);
      showError(err.message || 'Failed to fetch bank data');
    } finally {
      setBankLoading(false);
    }
  };

  // Fetch properties list for hotel dropdown
  const fetchProperties = async () => {
    try {
      setPropertiesLoading(true);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token) {
        throw new Error('No authentication token found');
      }
      if (!tenantId) {
        throw new Error('No tenant ID found');
      }

      // Fetch all properties using new API
      const apiUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/user-tenant-properties?tenant_id=${tenantId}&user_id=${localStorage.getItem('user_id')}`;

      const resp = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) {
        throw new Error(`Failed to fetch properties: ${resp.status} ${resp.statusText}`);
      }

      const data = await resp.json();

      if (Array.isArray(data)) {
        // Map the new API response to the expected format
        const mappedData = data.map(property => ({
          PROPERTY_ID: property.propertyId,
          PROPERTY_NAME: property.propertyName,
          PROPERTY_CODE: property.propertyCode,
          ...property
        }));
        setProperties(mappedData);
      } else {
        throw new Error('Invalid response format from properties API');
      }
    } catch (err) {
      console.error('Error fetching properties:', err);
      setProperties([]);
      // Don't show error toast for properties - just log it
    } finally {
      setPropertiesLoading(false);
    }
  };

  // Fetch mapping headers
  const fetchMappingHeaders = async () => {
    try {
      setMappingHeadersLoading(true);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id'); // Use tenant_id (UUID) for this API

      if (!token || !tenantId) {
        console.error('Missing authentication or tenant information');
        return;
      }

      // Try the correct endpoint pattern - query parameter instead of path parameter
      let apiUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon-mapping-headers/by-tenant?tenant_id=${tenantId}`;
      console.log('Fetching mapping headers from:', apiUrl);

      const resp = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) {
        // Try alternative endpoint pattern
        apiUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon-mapping-headers?tenant_id=${tenantId}`;
        console.log('Trying alternative endpoint:', apiUrl);

        const altResp = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!altResp.ok) {
          throw new Error(`Failed to fetch mapping headers: ${altResp.status} ${altResp.statusText}`);
        }

        const data = await altResp.json();
        console.log('Mapping headers API response (alternative):', data);

        if (Array.isArray(data)) {
          setMappingHeaders(data);
          console.log('Setting mapping headers:', data);
        } else {
          console.error('Invalid response format from mapping headers API');
          setMappingHeaders([]);
        }
      } else {
        const data = await resp.json();
        console.log('Mapping headers API response:', data);

        if (Array.isArray(data)) {
          setMappingHeaders(data);
          console.log('Setting mapping headers:', data);
        } else {
          console.error('Invalid response format from mapping headers API');
          setMappingHeaders([]);
        }
      }
    } catch (err) {
      console.error('Error fetching mapping headers:', err);

      // If we know a header exists (from previous error), create a temporary one
      if (selectedProperty && selectedBankMonthYear) {
        const selectedProp = properties.find(p => p.PROPERTY_NAME === selectedProperty || p.propertyName === selectedProperty);
        const propertyId = selectedProp?.PROPERTY_ID || selectedProp?.propertyId || selectedProperty;

        // Create a temporary header object with the information we have
        const tempHeader = {
          id: `temp-${propertyId}-${selectedBankMonthYear}`, // Temporary ID
          propertyId: propertyId,
          propertyName: selectedProperty,
          monthYear: selectedBankMonthYear,
          status: 'ACTIVE',
          balance: balanceData.bankBalance || 0,
          approveStatus: null,
          tenantId: localStorage.getItem('tenant_id'),
        };

        console.log('Creating temporary header object:', tempHeader);
        setMappingHeaders([tempHeader]);
      } else {
        setMappingHeaders([]);
      }
      // Don't show error toast - just log it
    } finally {
      setMappingHeadersLoading(false);
    }
  };

  // Note: Pagination is now handled client-side, so no need to fetch data on pagination changes

  // Fetch month year options for selected property
  // const fetchBankMonthYearOptions = async (propertyId) => {
  //   if (!propertyId) {
  //     setBankMonthYearOptions([]);
  //     setSelectedBankMonthYear('');
  //     return;
  //   }

  //   try {
  //     setBankMonthYearLoading(true);
  //     const token = localStorage.getItem('token');
  //     const tenantId = localStorage.getItem('tenant_id');

  //     if (!token || !tenantId) {
  //       console.error('Missing authentication or tenant information');
  //       return;
  //     }

  //     // Get a wide date range to find all available months
  //     const startDate = '2020-01-01'; // Or use a more reasonable start date
  //     const endDate = new Date().toISOString().split('T')[0]; // Today

  //     const apiUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/bank-statements/by-tenant-filter?tenant_id=${tenantId}&start_date=${startDate}&end_date=${endDate}&property_id=${propertyId}`;

  //     const resp = await fetch(apiUrl, {
  //       method: 'GET',
  //       headers: {
  //         'Authorization': `Bearer ${token}`,
  //         'Content-Type': 'application/json',
  //       },
  //     });

  //     if (!resp.ok) {
  //       throw new Error(`Failed to fetch month year options: ${resp.status} ${resp.statusText}`);
  //     }

  //     const data = await resp.json();

  //     if (Array.isArray(data)) {
  //       // Extract unique month/year from transaction dates
  //       const monthYears = [...new Set(data.map(item => {
  //         const date = new Date(item.transactionDate);
  //         const year = date.getFullYear();
  //         const month = (date.getMonth() + 1).toString().padStart(2, '0');
  //         return `${year}-${month}`;
  //       }))].sort().reverse();

  //       setBankMonthYearOptions(monthYears);
  //     } else {
  //       throw new Error('Invalid response format from bank statements API');
  //     }
  //   } catch (err) {
  //     console.error('Error fetching month year options:', err);
  //     setBankMonthYearOptions([]);
  //     // Don't show error toast - just log it
  //   } finally {
  //     setBankMonthYearLoading(false);
  //   }
  // };
  const fetchBankMonthYearOptions = async (propertyId) => {
    if (!propertyId) {
      setBankMonthYearOptions([]);
      setSelectedBankMonthYear('');
      setBankPropertyMappingHeaders([]);
      setPropertyGoLiveMonth('');
      return;
    }

    try {
      setBankMonthYearLoading(true);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token || !tenantId) {
        console.error('Missing authentication or tenant information');
        return;
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      // Go-live settings (optional) — drives which months are startable vs Historical
      let goLive = '';
      try {
        const settingsResp = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/status/settings/by-property?tenant_id=${tenantId}&property_id=${encodeURIComponent(propertyId)}`,
          { headers }
        );
        if (settingsResp.ok) {
          const settings = await settingsResp.json();
          goLive = settings?.goLiveMonth || '';
        }
      } catch {
        /* settings optional */
      }
      setPropertyGoLiveMonth(goLive);

      const apiUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon-mapping-headers/by-property?tenant_id=${tenantId}&property_id=${encodeURIComponent(propertyId)}`;

      const resp = await fetch(apiUrl, {
        method: 'GET',
        headers,
      });

      const headerMonths = [];
      if (resp.ok) {
        const data = await resp.json();
        if (Array.isArray(data)) {
          setBankPropertyMappingHeaders(data);
          data.forEach((item) => {
            if (item?.monthYear) headerMonths.push(item.monthYear);
          });
        } else {
          setBankPropertyMappingHeaders([]);
        }
      } else {
        setBankPropertyMappingHeaders([]);
      }

      // Always include go-live through current month so Nov appears even before a mapping exists
      const monthSet = new Set(headerMonths);
      const now = new Date();
      const currentYm = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      if (goLive) {
        const [gy, gm] = goLive.split('-').map(Number);
        let y = gy;
        let m = gm;
        const [cy, cm] = currentYm.split('-').map(Number);
        while (y < cy || (y === cy && m <= cm)) {
          monthSet.add(`${y}-${String(m).padStart(2, '0')}`);
          m += 1;
          if (m > 12) {
            m = 1;
            y += 1;
          }
        }
      }

      const pending = pendingNavMonthRef.current;
      if (pending) monthSet.add(pending);

      // Cap pre–go-live history in the dropdown to last 24 months
      const historyFloor = new Date(now.getFullYear(), now.getMonth() - 23, 1);
      const historyFloorYm = `${historyFloor.getFullYear()}-${String(historyFloor.getMonth() + 1).padStart(2, '0')}`;
      const monthYears = [...monthSet]
        .filter((ym) => {
          if (!goLive) return true;
          // Keep go-live and later always; keep older header months only within 24 months
          if (ym >= goLive) return true;
          return ym >= historyFloorYm;
        })
        .sort()
        .reverse();

      setBankMonthYearOptions(monthYears);
    } catch (err) {
      console.error('Error fetching month year options:', err);
      setBankMonthYearOptions([]);
      setBankPropertyMappingHeaders([]);
      setPropertyGoLiveMonth('');
    } finally {
      setBankMonthYearLoading(false);
    }
  };

  // Fetch month year options when property changes
  useEffect(() => {
    if (selectedProperty) {
      // Find property ID from properties list
      const selectedProp = properties.find(p => p.PROPERTY_NAME === selectedProperty || p.propertyName === selectedProperty);
      const propertyId = selectedProp?.PROPERTY_ID || selectedProp?.propertyId || selectedProperty;
      fetchBankMonthYearOptions(propertyId);
    } else {
      setBankMonthYearOptions([]);
      setSelectedBankMonthYear('');
      setBankPropertyMappingHeaders([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProperty, properties]);

  const bankAccountNoForSelectedMonth = useMemo(() => {
    if (!selectedBankMonthYear || !bankPropertyMappingHeaders.length) return '';
    const h = bankPropertyMappingHeaders.find((x) => x.monthYear === selectedBankMonthYear);
    const no = h?.bankAccountNo;
    return no != null && String(no).trim() !== '' ? String(no).trim() : '';
  }, [selectedBankMonthYear, bankPropertyMappingHeaders]);

  const isHistoricalMonth = useMemo(() => {
    if (!propertyGoLiveMonth || !selectedBankMonthYear) return false;
    return selectedBankMonthYear < propertyGoLiveMonth;
  }, [propertyGoLiveMonth, selectedBankMonthYear]);

  /** Historical months are view-only (load/view OK; map/approve/adjust blocked). */
  const isReconReadOnly = isHistoricalMonth;

  // Reset GL date filters when property changes
  useEffect(() => {
    setGlFilters(prev => ({
      ...prev,
      fromDate: '',
      toDate: ''
    }));
  }, [selectedProperty]);

  // Auto-populate GL date ranges when bank month/year changes
  useEffect(() => {
    if (selectedBankMonthYear) {
      // Parse month/year (format: YYYY-MM)
      const [year, month] = selectedBankMonthYear.split('-');
      
      // Calculate first day of month
      const fromDate = `${year}-${month}-01`;
      
      // Calculate last day of month
      const lastDay = new Date(year, parseInt(month), 0).getDate();
      const toDate = `${year}-${month}-${lastDay.toString().padStart(2, '0')}`;
      
      // Update GL filters
      setGlFilters(prev => ({
        ...prev,
        fromDate,
        toDate
      }));
    }
  }, [selectedBankMonthYear]);

  // Load properties and banks on mount
  useEffect(() => {
    fetchProperties();
    fetchBanks();
  }, []);

  // Apply property / month from status dashboard cell click
  useEffect(() => {
    const nav = location.state;
    if (!nav || dashboardNavApplied.current || propertiesLoading) return;
    if (!properties.length) return;

    const matched =
      properties.find((p) => String(p.PROPERTY_ID || p.propertyId) === String(nav.propertyId))
      || properties.find((p) => {
        const name = p.PROPERTY_NAME || p.propertyName || '';
        return nav.propertyName && name === nav.propertyName;
      })
      || null;

    const propertyName = matched?.PROPERTY_NAME || matched?.propertyName || nav.propertyName || '';
    if (!propertyName) return;

    if (nav.monthYear) {
      pendingNavMonthRef.current = nav.monthYear;
      setBankMonthYearOptions((prev) =>
        prev.includes(nav.monthYear) ? prev : [nav.monthYear, ...prev]
      );
      setSelectedBankMonthYear(nav.monthYear);
    }

    setSelectedProperty(propertyName);

    if (nav.reconStatus === 'BLOCKED' || nav.reconStatus === 'HISTORICAL') {
      setReconGateStatus(nav.reconStatus);
      setReconGateMessage(
        nav.blockedMessage
          || (nav.reconStatus === 'HISTORICAL'
            ? 'This month is Historical and read-only. An Admin override is required to change it.'
            : 'This month is blocked until the prior month is Completed.')
      );
    } else if (nav.reconStatus === 'COMPLETED') {
      setReconGateStatus(nav.reconStatus);
      setReconGateMessage('This month is Completed. It cannot be reopened without an Admin override.');
    } else {
      setReconGateStatus(nav.reconStatus || '');
      setReconGateMessage('');
    }
    dashboardNavApplied.current = true;
  }, [location.state, properties, propertiesLoading]);

  // After month options load for the navigated hotel, restore the dashboard month
  useEffect(() => {
    const pending = pendingNavMonthRef.current;
    if (!pending || !selectedProperty) return;
    if (!bankMonthYearOptions.includes(pending)) {
      setBankMonthYearOptions((prev) => (prev.includes(pending) ? prev : [pending, ...prev]));
      return;
    }
    if (selectedBankMonthYear !== pending) {
      setSelectedBankMonthYear(pending);
      return;
    }
    pendingNavMonthRef.current = '';
  }, [bankMonthYearOptions, selectedProperty, selectedBankMonthYear]);

  // Refresh GL grid when returning from reclassification screen
  useEffect(() => {
    if (!showReclassification && glDataLoaded) {
      // Only refresh if we were previously on reclassification screen
      // and GL data has been loaded before
      fetchGLData(glPaginationModel.page, glPaginationModel.pageSize);
    }
  }, [showReclassification]);

  // Fetch balance data from API
  const fetchBalanceData = async (propertyId = null, fromDate = null, toDate = null) => {
    try {
      setBalanceLoading(true);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token || !tenantId) {
        return null;
      }

      let apiUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/recon-mapping-details/balance?tenant_id=${tenantId}`;

      // Add optional parameters if provided
      if (propertyId) {
        apiUrl += `&property_id=${propertyId}`;
      }
      if (fromDate) {
        apiUrl += `&from_date=${fromDate}`;
      }
      if (toDate) {
        apiUrl += `&to_date=${toDate}`;
      }

      const resp = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (resp.ok) {
        const data = await resp.json();
        if (data.status === 'success') {
          const balanceInfo = {
            glBalance: data.glBalance || 0,
            bankBalance: data.bankBalance || 0,
            delta: data.delta || 0,
            endingBankBalance: data.bankBalance || 0,
            glDebits: data.glDebits || 0,
            glCredits: data.glCredits || 0,
            bankDebits: data.bankDebits || 0,
            bankCredits: data.bankCredits || 0,
            totalMappings: data.totalMappings || 0,
          };
          setBalanceData(balanceInfo);
          return balanceInfo; // Return the balance data
        }
      } else {
        // Handle API error response
        const errorData = await resp.json().catch(() => ({}));
        console.error('Balance API error:', errorData);
        // Clear stale data when API fails
        setBalanceData({
          glBalance: 0,
          bankBalance: 0,
          delta: 0,
          endingBankBalance: 0,
          glDebits: 0,
          glCredits: 0,
          bankDebits: 0,
          bankCredits: 0,
          totalMappings: 0,
        });
      }
      return null;
    } catch (err) {
      console.error('Error fetching balance data:', err);
      return null;
    } finally {
      setBalanceLoading(false);
    }
  };

  // Handle Approve button click - open confirmation dialog
  const handleApproveClick = () => {
    if (!canEdit || isReconReadOnly) {
      if (isReconReadOnly) {
        showError('This month is Historical (before go-live) and is view-only.');
      }
      return;
    }
    if (!headerId) {
      showError('No header found. Please load Bank data first.');
      return;
    }
    // Set the editable balance to current ending bank account balance
    setEditableEndingBalance(balanceData.endingBankBalance || 0);
    setApproveDialogOpen(true);
  };

  // Find headerId from selected property and monthYear
  useEffect(() => {
    console.log('Header ID useEffect triggered:', { selectedProperty, selectedBankMonthYear, mappingHeadersLength: mappingHeaders.length });

    if (selectedProperty && selectedBankMonthYear) {
      const selectedProp = properties.find(p => p.PROPERTY_NAME === selectedProperty || p.propertyName === selectedProperty);
      const propertyId = selectedProp?.PROPERTY_ID || selectedProp?.propertyId || selectedProperty;

      console.log('Looking for header with:', { propertyId, selectedProperty, monthYear: selectedBankMonthYear });
      console.log('Available headers:', mappingHeaders.map(h => ({ id: h.id, propertyId: h.propertyId, monthYear: h.monthYear })));

      const matchingHeader = mappingHeaders.find(h =>
        (h.propertyId === propertyId || h.propertyId === selectedProperty) &&
        h.monthYear === selectedBankMonthYear
      );

      console.log('Matching header found:', matchingHeader);

      if (matchingHeader) {
        console.log('Setting headerId to:', matchingHeader.id);
        setHeaderId(matchingHeader.id);
      } else {
        console.log('No matching header found, setting headerId to null');
        setHeaderId(null);
      }
    } else {
      console.log('Missing property or monthYear, setting headerId to null');
      setHeaderId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProperty, selectedBankMonthYear, mappingHeaders, properties]);

  // Current mapping header for GL account label: prefer mappingHeaders (after Bank Load), else by-property list (shows as soon as Month & Year is selected)
  const currentMappingHeaderForGl = useMemo(
    () => resolveMappingHeaderForGl(),
    [selectedProperty, selectedBankMonthYear, bankAccountNoForSelectedMonth, mappingHeaders, properties, bankPropertyMappingHeaders]
  );

  // Update cash recon mapping header (Approve) - called from confirmation dialog
  const updateMappingHeader = async () => {
    if (!canEdit) return;
    if (!headerId) {
      showError('No header found. Please load Bank data first.');
      return;
    }

    try {
      setApproving(true);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id'); // Fetch tenant_id (UUID) for header API

      if (!token || !tenantId) {
        throw new Error('Missing authentication or tenant information');
      }

      // Find the selected mapping header to get propertyId and monthYear
      const selectedHeader = mappingHeaders.find(h => h.id === headerId);
      if (!selectedHeader) {
        throw new Error('Selected mapping header not found');
      }

      const apiUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon-mapping-headers/${headerId}`;

      const requestBody = {
        propertyId: selectedHeader.propertyId,
        monthYear: selectedHeader.monthYear,
        balance: editableEndingBalance, // Use the edited ending bank account balance
        approveStatus: 'APPROVED',
        bankId: selectedHeader.bankId, // Use actual bank ID from mapping header
        bankAccountNo: selectedHeader.bankAccountNo, // Preserve bank account number during approval
        glAccountNo: selectedHeader.glAccountNo,
        glAccountName: selectedHeader.glAccountName,
        tenantId: tenantId,
        updatedBy: localStorage.getItem('user_id') || null,
        adminOverride: selectedHeader.adminOverride || false,
      };

      const resp = await fetch(apiUrl, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (resp.ok) {
        const data = await resp.json();

        // Call bulk approval APIs after successful header approval
        try {
          // Get property ID from selected property
          const selectedProp = properties.find(p => p.PROPERTY_NAME === selectedProperty || p.propertyName === selectedProperty);
          const propertyId = selectedProp?.PROPERTY_ID || selectedProp?.propertyId || selectedProperty;

          // Get approvedBy from localStorage or use a default
          const approvedBy = localStorage.getItem('username');

          // Extract IDs based on approval scope
          let bankStatementIds = [];
          let glTransactionIds = [];

          console.log('Approval scope:', approvalScope); // Debug log

          if (approvalScope === 'mapped_records') {
            // Only include mapped records
            bankStatementIds = bankRows
              .filter(row => row.originalData?.mappedGlIds && row.originalData.mappedGlIds.length > 0)
              .map(row => row.originalData?.id || row.id)
              .filter(id => id);
          } else {
            // Include all records (selected period)
            bankStatementIds = bankRows.map(row => row.originalData?.id || row.id).filter(id => id);
          }

          // Walk every GL page so approval is not limited to the rows on screen.
          const glRowsForApproval = await loadAllFilteredGlRows();
          if (approvalScope === 'mapped_records') {
            glTransactionIds = glRowsForApproval
              .filter(row => row.originalData?.mappedBankIds && row.originalData.mappedBankIds.length > 0)
              .map(row => row.originalData?.id || row.id)
              .filter(id => id);
          } else {
            glTransactionIds = glRowsForApproval.map(row => row.originalData?.id || row.id).filter(id => id);
          }

          // Call bank statements bulk approval API
          if (bankStatementIds.length > 0) {
            const bankApprovalUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/bank-statements/bulk-approval`;
            const bankApprovalBody = {
              tenantId: tenantId,
              propertyId: propertyId,
              bankStatementIds: bankStatementIds,
              approvalStatus: 'APPROVED',
              approvedBy: approvedBy
            };

            const bankResp = await fetch(bankApprovalUrl, {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(bankApprovalBody),
            });

            if (bankResp.ok) {
              const bankResult = await bankResp.json();
              console.log('Bank statements bulk approval successful:', bankResult);
            } else {
              console.error('Bank statements bulk approval failed:', await bankResp.text());
            }
          }

          // Call GL transactions bulk approval API
          if (glTransactionIds.length > 0) {
            const glApprovalUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/gl-transactions/bulk-approval`;
            const glApprovalBody = {
              tenantId: tenantId,
              propertyId: propertyId,
              glTransactionIds: glTransactionIds,
              approvalStatus: 'APPROVED',
              approvedBy: approvedBy
            };

            const glResp = await fetch(glApprovalUrl, {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(glApprovalBody),
            });

            if (glResp.ok) {
              const glResult = await glResp.json();
              console.log('GL transactions bulk approval successful:', glResult);
            } else {
              console.error('GL transactions bulk approval failed:', await glResp.text());
            }
          }
        } catch (bulkErr) {
          console.error('Error in bulk approval APIs:', bulkErr);
          // Don't fail the main approval process if bulk approvals fail
        }

        showSuccess('Reconciliation approved successfully');
        setApproveDialogOpen(false);
        // Optionally update headerId if response has new ID
        if (data.id) {
          setHeaderId(data.id);
        }
        // Refresh mapping headers to get updated status
        fetchMappingHeaders();
        // Refresh bank and GL data to show updated approval status
        fetchBankData(0, 100);
        setGlPaginationModel((prev) => ({ ...prev, page: 0 }));
        fetchGLData(0, glPaginationModel.pageSize || 100);
      } else {
        const errorText = await resp.text();
        throw new Error(`Failed to approve: ${errorText}`);
      }
    } catch (err) {
      console.error('Error updating mapping header:', err);
      showError(err.message || 'Failed to approve reconciliation');
    } finally {
      setApproving(false);
    }
  };

  // Fetch bank data chase summary to get CLOSING_AVAILABLE for ending bank account balance
  const fetchBankDataChaseSummary = async (bankAccountNo) => {
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_integration');

      if (!token || !tenantId) {
        console.error('Missing authentication or tenant information');
        return;
      }

      // Calculate date range from selected month/year
      const [year, month] = selectedBankMonthYear.split('-');
      const startDate = `${year}-${month}-01`;
      const lastDay = new Date(year, parseInt(month), 0).getDate();
      const endDate = `${year}-${month}-${lastDay.toString().padStart(2, '0')}`;

      const apiUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/cash-recon/bank-data-chase-summary?tenant_id=${tenantId}&start_date=${startDate}&end_date=${endDate}&customer_account_number=${bankAccountNo}`;
      console.log('Fetching bank data chase summary from:', apiUrl);

      const resp = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) {
        console.error(`Failed to fetch bank data chase summary: ${resp.status} ${resp.statusText}`);
        return;
      }

      const data = await resp.json();
      console.log('Bank data chase summary API response:', data);

      if (data.status === 'success' && data.data && data.data.length > 0) {
        // Find the record with the latest AS_OF_DATE
        const latestRecord = data.data.reduce((latest, current) => {
          if (!latest) return current;
          return new Date(current.AS_OF_DATE) > new Date(latest.AS_OF_DATE) ? current : latest;
        });

        // Extract CLOSING_AVAILABLE and update balanceData
        const closingAvailable = latestRecord.CLOSING_AVAILABLE || 0;
        console.log('Latest record AS_OF_DATE:', latestRecord.AS_OF_DATE);
        console.log('CLOSING_AVAILABLE value:', closingAvailable);

        // Update balanceData with the new ending bank account balance
        setBalanceData(prev => ({
          ...prev,
          endingBankBalance: closingAvailable
        }));
      } else {
        console.log('No data found in bank data chase summary response');
      }
    } catch (err) {
      console.error('Error fetching bank data chase summary:', err);
      // Don't show error toast - just log it
    }
  };

  // Fetch mapping headers by filter (specific API for bank section)
  const fetchMappingHeadersByFilter = async () => {
    if (!selectedProperty || !selectedBankMonthYear) {
      console.log('Missing required parameters for mapping headers filter');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token || !tenantId) {
        console.error('Missing authentication or tenant information');
        return;
      }

      // Get property ID
      const selectedProp = properties.find(p => p.PROPERTY_NAME === selectedProperty || p.propertyName === selectedProperty);
      const propertyId = selectedProp?.PROPERTY_ID || selectedProp?.propertyId || selectedProperty;

      let apiUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon-mapping-headers/filter?tenant_id=${tenantId}&property_id=${propertyId}&month_year=${selectedBankMonthYear}`;
      if (bankAccountNoForSelectedMonth) {
        apiUrl += `&bank_account_number=${encodeURIComponent(bankAccountNoForSelectedMonth)}`;
      }
      console.log('Fetching mapping headers by filter from:', apiUrl);

      const resp = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) {
        console.error(`Failed to fetch mapping headers by filter: ${resp.status} ${resp.statusText}`);
        return;
      }

      const data = await resp.json();
      console.log('Mapping headers filter API response:', data);

      if (Array.isArray(data) && data.length > 0) {
        // Update mapping headers with the filtered results
        setMappingHeaders(data);
        console.log('Setting mapping headers from filter API:', data);

        // Extract bankAccountNo from the first header and call bank data chase summary API
        const bankAccountNo = data[0]?.bankAccountNo;
        if (bankAccountNo) {
          await fetchBankDataChaseSummary(bankAccountNo);
        }
      } else {
        console.log('No mapping headers found for the specified criteria');
        // Keep existing headers if filter returns empty
      }
    } catch (err) {
      console.error('Error fetching mapping headers by filter:', err);
      // Don't show error toast - just log it
    }
  };

  // Load button handlers
  const handleLoadBankData = async () => {
    if (!selectedProperty || !selectedBankMonthYear) {
      showError('Please select Property and Month & Year to load Bank data');
      return;
    }

    setBankPaginationModel({ page: 0, pageSize: 100 });
    await fetchBankData(0, 100);

    // Calculate date range from selected month/year
    const [year, month] = selectedBankMonthYear.split('-');
    const startDate = `${year}-${month}-01`;
    const lastDay = new Date(year, parseInt(month), 0).getDate();
    const endDate = `${year}-${month}-${lastDay.toString().padStart(2, '0')}`;

    // Get property ID
    const selectedProp = properties.find(p => p.PROPERTY_NAME === selectedProperty || p.propertyName === selectedProperty);
    const propertyId = selectedProp?.PROPERTY_ID || selectedProp?.propertyId || selectedProperty;

    // Fetch balance with property and date filters
    await fetchBalanceData(propertyId, startDate, endDate);
    // Fetch headers so headerId gets resolved
    await fetchMappingHeaders();
    // Fetch headers by filter for bank-specific data (when we have effective bank from headers)
    await fetchMappingHeadersByFilter();
  };

  // Helper function to find GL transaction description by ID
  const getGLTransactionDescription = (glId) => {
    const glTransaction = glRows.find(row => row.originalData?.id === glId);
    return glTransaction?.originalData?.description || glTransaction?.description || 'No description';
  };

  // Helper function to find Bank transaction description by ID
  const getBankTransactionDescription = (bankId) => {
    const bankTransaction = bankRows.find(row => row.originalData?.id === bankId);
    return bankTransaction?.originalData?.description || bankTransaction?.description || 'No description';
  };

  // Helper function to get GL transaction amount by ID
  const getGLTransactionAmount = (glId) => {
    const glTransaction = glRows.find(row => row.originalData?.id === glId);
    return glTransaction?.originalData?.debitAmount || glTransaction?.originalData?.creditAmount || 0;
  };

  // Helper function to get Bank transaction amount by ID
  const getBankTransactionAmount = (bankId) => {
    const bankTransaction = bankRows.find(row => row.originalData?.id === bankId);
    return bankTransaction?.originalData?.debitAmount || bankTransaction?.originalData?.creditAmount || 0;
  };

  // Helper function to get mapped amount from arrays
  const getMappedAmount = (mappedAmounts, index) => {
    return mappedAmounts?.[index] || 0;
  };

  const handleLoadGLData = () => {
    if (!selectedProperty || !glFilters.fromDate || !glFilters.toDate) {
      showError('Please select Property, From Date, and To Date to load GL data');
      return;
    }
    const pageSize = glPaginationModel.pageSize || 100;
    setGlPaginationModel({ page: 0, pageSize });
    fetchGLData(0, pageSize);
  };

  // Handle Add Bank
  const handleAddBank = () => {
    setBankDrawerOpen(true);
  };

  // Handle Chart of Accounts
  const handleChartOfAccounts = () => {
    setChartOfAccountsDrawerOpen(true);
  };

  // Handle GL Transactions
  const handleGLTransactions = () => {
    setGlTransactionDrawerOpen(true);
  };

  // Recon status takes precedence over mappedStatus for auto-match eligibility.
  const isEligibleForAutoMatch = row => {
    const recon = row.originalData?.reconStatus;
    if (recon != null && String(recon).trim() !== '') {
      const up = String(recon).trim().toUpperCase();
      if (up === 'UNMATCHED') return true;
      if (up === 'AUTO_MATCHED' || up === 'MANUAL_MATCHED' || up === 'MATCHED') return false;
    }
    return !row.isMapped;
  };

  // Auto matching function. GL candidates are every page of the current filter.
  const performAutoMatching = async () => {
    try {
      setAutoMatchingLoading(true);
      const matchedPairs = [];
      const usedBankIndices = new Set();
      const usedGlIndices = new Set();

      // Prefer reconStatus over isMapped when deciding who can be auto-matched
      const unmappedBankRows = bankRows.filter(isEligibleForAutoMatch);
      const unmappedGlRows = (await loadAllFilteredGlRows()).filter(isEligibleForAutoMatch);

      // Iterate through bank records
      for (let i = 0; i < unmappedBankRows.length; i++) {
        if (usedBankIndices.has(i)) continue;

        const bankRow = unmappedBankRows[i];
        const bankDebit = Math.abs(bankRow.debit || 0);
        const bankCredit = Math.abs(bankRow.credit || 0);

        // Determine which GL field to match against
        let targetGlField = null;
        let bankAmount = 0;

        if (bankDebit > 0 && bankCredit === 0) {
          // Bank has debit, should match with GL credit
          targetGlField = 'credit';
          bankAmount = bankDebit;
        } else if (bankCredit > 0 && bankDebit === 0) {
          // Bank has credit, should match with GL debit
          targetGlField = 'debit';
          bankAmount = bankCredit;
        }

        if (!targetGlField || bankAmount === 0) continue;

        // Find matching GL record with same value
        for (let j = 0; j < unmappedGlRows.length; j++) {
          if (usedGlIndices.has(j)) continue;

          const glRow = unmappedGlRows[j];
          const glDebit = Math.abs(glRow.debit || 0);
          const glCredit = Math.abs(glRow.credit || 0);

          // Check if GL record has the opposite field with value
          let glAmount = 0;
          if (targetGlField === 'debit' && glDebit > 0 && glCredit === 0) {
            glAmount = glDebit;
          } else if (targetGlField === 'credit' && glCredit > 0 && glDebit === 0) {
            glAmount = glCredit;
          }

          // Check if amounts match (within 0.01 for floating point precision)
          if (glAmount > 0 && Math.abs(bankAmount - glAmount) < 0.01) {
            matchedPairs.push({
              bankRow: bankRow,
              glRow: glRow,
              bankAmount: bankAmount,
              glAmount: glAmount,
              bankDescription: bankRow.description || '',
              glDescription: glRow.description || '',
              bankDocNo: bankRow.docNo || '',
              glDocNo: glRow.docNo || '',
            });
            usedBankIndices.add(i);
            usedGlIndices.add(j);
            break; // Match found, move to next bank record
          }
        }
      }

      setAutoMatchedPairs(matchedPairs);
    } catch (err) {
      console.error('Error performing auto matching:', err);
      showError('Failed to perform auto matching');
    } finally {
      setAutoMatchingLoading(false);
    }
  };

  // Handle auto matching dialog open
  const handleAutoMatchingDialogOpen = () => {
    if (isReconReadOnly) {
      showError('This month is Historical (before go-live) and is view-only.');
      return;
    }
    setAutoMatchingDialogOpen(true);
    // Perform auto matching when dialog opens
    performAutoMatching();
  };

  // Save auto matched pairs
  const handleSaveAutoMatches = async () => {
    if (autoMatchedPairs.length === 0) {
      showError('No matches to save');
      return;
    }

    try {
      setAutoMatchingLoading(true);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token || !tenantId) {
        throw new Error('Missing authentication or tenant information');
      }

      // Get property ID
      const selectedProp = properties.find(p => p.PROPERTY_NAME === selectedProperty || p.propertyName === selectedProperty);
      const propertyId = selectedProp?.PROPERTY_ID || selectedProp?.propertyId || selectedProperty;

      // Build payload array for all matched pairs using the correct format
      const payload = autoMatchedPairs.map(pair => {
        const bankStatementId = pair.bankRow.originalData?.id;
        const glTransactionId = pair.glRow.originalData?.id;

        return {
          bankStatementId: bankStatementId,
          glTransactionId: glTransactionId,
          tenantId: tenantId,
        };
      });

      // Step 1: Call the correct bulk API for all mappings at once
      const apiUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/recon-mapping-details/bulk`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to save matches: ${response.status}`);
      }

      const savedMappings = await response.json();

      // Extract the reconId from the first saved mapping (if available)
      const reconId = Array.isArray(savedMappings)
        ? savedMappings[0]?.id || savedMappings[0]?.reconId || null
        : savedMappings?.id || savedMappings?.reconId || null;

      // Collect all bank and GL IDs for bulk reconciliation
      const bankStatementIds = autoMatchedPairs.map(pair => pair.bankRow.originalData?.id).filter(Boolean);
      const glTransactionIds = autoMatchedPairs.map(pair => pair.glRow.originalData?.id).filter(Boolean);

      const reconPayloadBase = {
        tenantId: tenantId,
        propertyId: propertyId,
        reconStatus: 'AUTO_MATCHED',
        matchedBy: 'SYSTEM',
        reconId: reconId,
      };

      // Step 2: Bulk reconciliation for Bank statements
      if (bankStatementIds.length > 0) {
        try {
          const bankReconResp = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/bank-statements/bulk-reconciliation`,
            {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                ...reconPayloadBase,
                bankStatementIds: bankStatementIds,
              }),
            }
          );
          if (!bankReconResp.ok) {
            console.error('Bank bulk reconciliation failed:', await bankReconResp.text());
          }
        } catch (err) {
          console.error('Error in bank bulk reconciliation:', err);
        }
      }

      // Step 3: Bulk reconciliation for GL transactions
      if (glTransactionIds.length > 0) {
        try {
          const glReconResp = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/gl-transactions/bulk-reconciliation`,
            {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                ...reconPayloadBase,
                glTransactionIds: glTransactionIds,
              }),
            }
          );
          if (!glReconResp.ok) {
            console.error('GL bulk reconciliation failed:', await glReconResp.text());
          }
        } catch (err) {
          console.error('Error in GL bulk reconciliation:', err);
        }
      }

      showSuccess(`Successfully auto-matched ${autoMatchedPairs.length} record(s)`);
      setAutoMatchingDialogOpen(false);
      setAutoMatchedPairs([]);

      // Refresh the main grids
      fetchGLData(glPaginationModel.page, glPaginationModel.pageSize);
      fetchBankData(bankPaginationModel.page, bankPaginationModel.pageSize);

      // Refresh balance data after auto matching save
      if (selectedProperty && selectedBankMonthYear) {
        const [year, month] = selectedBankMonthYear.split('-');
        const startDate = `${year}-${month}-01`;
        const lastDay = new Date(year, parseInt(month), 0).getDate();
        const endDate = `${year}-${month}-${lastDay.toString().padStart(2, '0')}`;
        fetchBalanceData(propertyId, startDate, endDate);
      }
    } catch (err) {
      showError(err.message || 'Failed to save auto matches');
    } finally {
      setAutoMatchingLoading(false);
    }
  };

  // Fetch existing mappings
  // Helper function to check if a record is mapped and get the mapping
  const getMappingForRecord = (rowId) => {
    return existingMappings.find(mapping => {
      const mappedId = !isMappingFromGL
        ? mapping.glTransactionId
        : mapping.bankStatementId;
      return mappedId === rowId;
    });
  };

  // Helper function to check if a record is mapped
  const isRecordMapped = (rowId) => {
    return existingMappings.some(mapping => {
      const mappedId = !isMappingFromGL
        ? mapping.glTransactionId
        : mapping.bankStatementId;
      return mappedId === rowId;
    });
  };

  const fetchExistingMappings = async (targetRows) => {
    if (!selectedSourceRow) return;

    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token || !tenantId) {
        return;
      }

      // Call the by-tenant endpoint to get all mappings for the tenant
      const apiUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/recon-mapping-details/by-tenant?tenant_id=${tenantId}`;

      console.log('*** fetchExistingMappings API call ***');
      console.log('URL:', apiUrl);
      console.log('selectedSourceRow:', selectedSourceRow);

      const resp = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Response status:', resp.status);
      console.log('Response ok:', resp.ok);

      if (resp.ok) {
        const data = await resp.json();
        console.log('API response data:', data);
        console.log('Data type:', typeof data);
        console.log('Is array:', Array.isArray(data));
        console.log('Data length:', data?.length);

        if (Array.isArray(data)) {
          // Store full mapping objects
          setExistingMappings(data);
          console.log('Set existingMappings to:', data.length, 'items');

          // Extract the IDs of mapped records for compatibility
          const mappedIds = data.map(mapping => {
            if (!isMappingFromGL) {
              // If mapping from Bank, get glTransactionId (GL records)
              return mapping.glTransactionId;
            } else {
              // If mapping from GL, get bankStatementId (Bank records)
              return mapping.bankStatementId;
            }
          });

          console.log('Mapped IDs:', mappedIds);

          // Pre-select already mapped rows in target grid
          if (targetRows && targetRows.length > 0) {
            const mappedRows = targetRows.filter(row => {
              const rowId = row.originalData?.id;
              return mappedIds.includes(rowId);
            });
            // Don't pre-select already mapped rows - only manual selections should be counted
            // setSelectedTargetRows(mappedRows);
            console.log('Already mapped rows (not pre-selected):', mappedRows.length);
          }
        } else {
          console.log('API response is not an array');
        }
      } else {
        console.log('API call failed');
      }
    } catch (err) {
      console.error('Error fetching existing mappings:', err);
      // Don't show error to user, just log it
    }
  };

  // Open mapping dialog
  const handleOpenMappingDialog = (row, isFromGL) => {
    if (isReconReadOnly) {
      showError('This month is Historical (before go-live) and is view-only.');
      return;
    }
    setSelectedSourceRow(row);
    setIsMappingFromGL(isFromGL);
    setSelectedTargetRows([]);
    setTargetGridSearch('');
    setTargetGridAllRows([]); // Clear unfiltered data when opening dialog
    setExistingMappings([]);
    setEnableDeleteMode(false); // Reset delete mode when opening dialog
    setMappingDialogOpen(true);

    // Load target grid data
    if (isFromGL) {
      // If mapping from GL, load Bank data
      fetchTargetGridData(0, 100, false);
    } else {
      // If mapping from Bank, load GL data
      fetchTargetGridData(0, 100, true, '');
    }
  };

  // Fetch existing mappings when dialog opens and source row is set
  useEffect(() => {
    if (mappingDialogOpen && selectedSourceRow) {
      fetchExistingMappings([]);
    }
  }, [mappingDialogOpen, selectedSourceRow]);

  // Close mapping dialog
  const handleCloseMappingDialog = () => {
    setMappingDialogOpen(false);
    setSelectedSourceRow(null);
    setSelectedTargetRows([]);
    setTargetGridSearch('');
    setTargetGridRows([]);
    setTargetGridAllRows([]); // Clear unfiltered data
    setEnableDeleteMode(false); // Reset delete mode when closing dialog
  };

  // Handle reclassify (soft delete) for GL transactions
  const handleReclassify = async (glTransactionId) => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      const apiUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/gl-transactions/soft-delete/${glTransactionId}`;

      const response = await fetch(apiUrl, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to reclassify transaction: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Reclassify response:', data);
      
      showSuccess('Transaction reclassified successfully');
      
      // Refresh GL grid after successful reclassification
      fetchGLData(glPaginationModel.page, glPaginationModel.pageSize);
      
    } catch (err) {
      console.error('Error reclassifying transaction:', err);
      showError(err.message || 'Failed to reclassify transaction');
    }
  };

  // Handle bulk reclassify using bulk API
  const handleBulkReclassify = async () => {
    try {
      setReclassifyingBulk(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      const apiUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/gl-transactions/soft-delete/bulk`;
      const transactionIds = selectedGlRows.map((id) => String(id).trim()).filter(Boolean);

      const response = await fetch(apiUrl, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(transactionIds),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const detail =
          (typeof data?.message === 'string' && data.message.trim()) ||
          (typeof data?.error === 'string' && data.error.trim()) ||
          `${response.status} ${response.statusText}`;
        throw new Error(`Failed to bulk reclassify transactions: ${detail}`);
      }

      console.log('Bulk reclassify response:', data);
      const reclassifiedCount = Array.isArray(data) ? data.length : selectedGlRows.length;
      showSuccess(`${reclassifiedCount} transaction(s) reclassified successfully`);
      
      // Refresh GL grid after successful reclassification
      fetchGLData(glPaginationModel.page, glPaginationModel.pageSize);
      
      // Reset selection and close dialog
      setSelectedGlRows([]);
      setBulkReclassifyDialogOpen(false);
      
    } catch (err) {
      console.error('Error bulk reclassifying transactions:', err);
      showError(err.message || 'Failed to reclassify transactions');
    } finally {
      setReclassifyingBulk(false);
    }
  };

  // Open Push to another date dialog
  const handleOpenPushToDateDialog = (row) => {
    setGlRecordForPush(row);
    setPushDate(row.originalData?.pushDate || '');
    setPushToDateDialogOpen(true);
  };

  // Push GL transaction to another date (PUT API)
  const handlePushToDate = async () => {
    if (!glRecordForPush?.id) return;
    if (!pushDate || !pushDate.trim()) {
      showError('Please select a push date.');
      return;
    }
    setPushing(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';
      const url = `${baseUrl}conductor/cash-recon/gl-transactions/${glRecordForPush.id}/push`;
      const resp = await fetch(url, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ pushDate: pushDate.trim() }),
      });
      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        throw new Error(errData.message || errData.error || `Failed to push: ${resp.status}`);
      }
      showSuccess('Transaction pushed to date successfully');
      setPushToDateDialogOpen(false);
      setGlRecordForPush(null);
      setPushDate('');
      fetchGLData(glPaginationModel.page, glPaginationModel.pageSize);
    } catch (err) {
      showError(err.message || 'Failed to push to date');
    } finally {
      setPushing(false);
    }
  };

  // Fetch target grid data (Bank or GL based on isGL parameter)
  const fetchTargetGridData = async (page, pageSize, isGL = false, searchText) => {
    try {
      setTargetGridLoading(true);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token || !tenantId) {
        throw new Error('Missing authentication or tenant information');
      }

      let apiUrl;
      if (isGL) {
        const safePage = Number.isFinite(page) ? page : 0;
        const safePageSize = Number.isFinite(pageSize) && pageSize > 0 ? pageSize : 100;
        apiUrl = buildGlMappingFilterUrl(tenantId, {
          stateToken: apiMappingState(glFilters.state),
          limit: safePageSize,
          offset: safePage * safePageSize,
          search: searchText != null ? searchText : targetGridSearch,
        });
      } else {
        // Fetching Bank data - use new Bank API
        if (!selectedProperty || !selectedBankMonthYear) {
          throw new Error('Property and Month & Year must be selected');
        }

        // Convert month/year to date range
        const [year, month] = selectedBankMonthYear.split('-');
        const startDate = `${year}-${month}-01`;
        const lastDay = new Date(year, parseInt(month), 0).getDate(); // Get last day of month
        const endDate = `${year}-${month}-${lastDay.toString().padStart(2, '0')}`;

        // Get property ID from properties list
        const selectedProp = properties.find(p => p.PROPERTY_NAME === selectedProperty || p.propertyName === selectedProperty);
        const propertyId = selectedProp?.PROPERTY_ID || selectedProp?.propertyId || selectedProperty;

        apiUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/bank-statements/by-tenant-mapping-filter?tenant_id=${tenantId}&start_date=${startDate}&end_date=${endDate}&property_id=${propertyId}&state=${apiMappingState(bankFilters.state)}`;
        if (bankAccountNoForSelectedMonth) {
          apiUrl += `&bank_account_number=${encodeURIComponent(bankAccountNumberForApiQuery(bankAccountNoForSelectedMonth))}`;
        }
      }

      // Note: Search is handled client-side for now
      // If API supports search parameter, uncomment below:
      // if (targetGridSearch) {
      //   apiUrl += `&search=${encodeURIComponent(targetGridSearch)}`;
      // }

      if (isGL) {
        const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';
        apiUrl = await appendCashReconLedgerCoaQueryParams(apiUrl, { baseUrl, tenantId, token });
      }

      const resp = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) {
        throw new Error(`Failed to fetch data: ${resp.status} ${resp.statusText}`);
      }

      const data = await resp.json();
      const toTargetRow = (item, index) => ({
        id: item.id || index + 1,
        description: item.description || '',
        date: item.transactionDate || '',
        docNo: item.referenceNo || '',
        debit: item.debitAmount || 0,
        credit: item.creditAmount || 0,
        remarks: item.notes || '',
        selected: false,
        originalData: item,
      });

      if (isGL) {
        const pageResult = unwrapGlTransactionPage(data);
        if (!pageResult) {
          throw new Error('Invalid response format from API');
        }
        setTargetGridAllRows([]);
        setTargetGridRows(pageResult.content.map(toTargetRow));
        setTargetGridRowCount(pageResult.total);
      } else if (Array.isArray(data)) {
        let processedData = data.map(toTargetRow);

        // Store all unfiltered data
        setTargetGridAllRows(processedData);

        // Apply search filter if search term exists
        if (targetGridSearch) {
          const searchLower = targetGridSearch.toLowerCase();
          processedData = processedData.filter(row =>
            (row.description || '').toLowerCase().includes(searchLower) ||
            (row.date || '').toLowerCase().includes(searchLower) ||
            (row.docNo || '').toLowerCase().includes(searchLower) ||
            String(row.debit || '').includes(searchLower) ||
            String(row.credit || '').includes(searchLower)
          );
        }

        setTargetGridRows(processedData);

        // Note: fetchExistingMappings is now called immediately in handleOpenMappingDialog
      } else {
        throw new Error('Invalid response format from API');
      }
    } catch (err) {
      showError(err.message || 'Failed to fetch data');
      setTargetGridRows([]);
    } finally {
      setTargetGridLoading(false);
    }
  };

  // Bank target grid filters the loaded list. GL search is sent to the API.
  useEffect(() => {
    if (!mappingDialogOpen || !isMappingFromGL || targetGridAllRows.length === 0) return;

    const timeoutId = setTimeout(() => {
      if (targetGridSearch) {
        const searchLower = targetGridSearch.toLowerCase();
        const filteredData = targetGridAllRows.filter(row =>
          (row.description || '').toLowerCase().includes(searchLower) ||
          (row.date || '').toLowerCase().includes(searchLower) ||
          (row.docNo || '').toLowerCase().includes(searchLower) ||
          String(row.debit || '').includes(searchLower) ||
          String(row.credit || '').includes(searchLower)
        );
        setTargetGridRows(filteredData);
      } else {
        setTargetGridRows(targetGridAllRows);
      }
      // Reset to first page when search changes
      setTargetGridPaginationModel(prev => ({ page: 0, pageSize: prev.pageSize }));
    }, 300);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetGridSearch, targetGridAllRows, mappingDialogOpen, isMappingFromGL]);

  // GL target grid: ask the server for the next page of matches instead of holding every row.
  useEffect(() => {
    if (!mappingDialogOpen || isMappingFromGL) return;
    const timeoutId = setTimeout(() => {
      setTargetGridPaginationModel((prev) => ({ ...prev, page: 0 }));
      fetchTargetGridData(0, targetGridPaginationModel.pageSize, true, targetGridSearch);
    }, 300);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetGridSearch]);

  // When existing mappings change, update selectedTargetRows for current page
  useEffect(() => {
    if (mappingDialogOpen && existingMappings.length > 0 && targetGridRows.length > 0) {
      const mappedRows = targetGridRows.filter(row => {
        const rowId = row.originalData?.id;
        return isRecordMapped(rowId);
      });

      // Don't merge already mapped rows with selectedTargetRows
      // Only manual selections should be counted in calculations
      // setSelectedTargetRows(prev => {
      //   const existingIds = prev.map(r => r.id);
      //   const newMappedRows = mappedRows.filter(r => !existingIds.includes(r.id));
      //   return [...prev, ...newMappedRows];
      // });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingMappings, targetGridRows, mappingDialogOpen]);

  // Automatically disable delete mode when there are no mappings
  useEffect(() => {
    if (existingMappings.length === 0 && enableDeleteMode) {
      setEnableDeleteMode(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingMappings.length]);

  // Calculate mapping totals and delta
  const calculateMappingTotals = () => {
    if (!selectedSourceRow) {
      return { sourceDebit: 0, sourceCredit: 0, targetDebit: 0, targetCredit: 0, delta: 0 };
    }

    // Check if source row is already fully mapped - if so, delta should be 0
    const isSourceFullyMapped = selectedSourceRow.originalData?.mappedStatus === 'MAPPED' && 
      ((selectedSourceRow.originalData?.mappedBankIds && selectedSourceRow.originalData.mappedBankIds.length > 0) ||
       (selectedSourceRow.originalData?.mappedGlIds && selectedSourceRow.originalData.mappedGlIds.length > 0));

    if (isSourceFullyMapped) {
      const sourceDebit = selectedSourceRow.debit || 0;
      const sourceCredit = selectedSourceRow.credit || 0;
      return { 
        sourceDebit, 
        sourceCredit, 
        targetDebit: 0, 
        targetCredit: 0, 
        delta: 0 
      };
    }

    const sourceDebit = selectedSourceRow.debit || 0;
    const sourceCredit = selectedSourceRow.credit || 0;

    // For the display, show values from currently selected records
    // Only calculate target values when target records are actually selected
    let targetDebit = 0;
    let targetCredit = 0;
    let delta = 0;

    // Check if any target records are selected
    const hasSelectedTargets = selectedTargetRows && selectedTargetRows.length > 0;

    if (hasSelectedTargets) {
      // Sum up selected target rows only when records are actually selected
      selectedTargetRows.forEach(row => {
        targetDebit += row.debit || 0;
        targetCredit += row.credit || 0;
      });

      // Calculate delta based on mapping direction
      if (isMappingFromGL) {
        // GL to Bank: GL debit should match Bank credit, GL credit should match Bank debit
        if (Math.abs(sourceDebit) > 0 && Math.abs(sourceCredit) === 0) {
          // GL has debit, Bank should have credit
          delta = Math.abs(sourceDebit) - Math.abs(targetCredit);
        } else if (Math.abs(sourceCredit) > 0 && Math.abs(sourceDebit) === 0) {
          // GL has credit, Bank should have debit
          delta = Math.abs(sourceCredit) - Math.abs(targetDebit);
        }
      } else {
        // Bank to GL: Bank debit should match GL credit, Bank credit should match GL debit
        if (Math.abs(sourceDebit) > 0 && Math.abs(sourceCredit) === 0) {
          // Bank has debit, GL should have credit
          delta = Math.abs(sourceDebit) - Math.abs(targetCredit);
        } else if (Math.abs(sourceCredit) > 0 && Math.abs(sourceDebit) === 0) {
          // Bank has credit, GL should have debit
          delta = Math.abs(sourceCredit) - Math.abs(targetDebit);
        }
      }
    } else {
      // No target records selected - delta should be the full source amount
      if (Math.abs(sourceDebit) > 0 && Math.abs(sourceCredit) === 0) {
        delta = Math.abs(sourceDebit);
      } else if (Math.abs(sourceCredit) > 0 && Math.abs(sourceDebit) === 0) {
        delta = Math.abs(sourceCredit);
      }
    }

    return {
      sourceDebit,
      sourceCredit,
      targetDebit,
      targetCredit,
      delta,
    };
  };

  // Save mapping function with new API
  // const handleSaveMapping = async () => {
  //   if (!selectedSourceRow || selectedTargetRows.length === 0) {
  //     showError('Please select at least one record to map');
  //     return;
  //   }

  //   setSavingMapping(true);

  //   try {
  //     const token = localStorage.getItem('token');
  //     const tenantId = localStorage.getItem('tenant_id');

  //     if (!token || !tenantId) {
  //       throw new Error('Missing authentication or tenant information');
  //     }

  //     // Filter out already-mapped rows to avoid duplicates
  //     const newTargetRows = selectedTargetRows.filter(targetRow => {
  //       const rowId = !isMappingFromGL
  //         ? targetRow.originalData?.id
  //         : targetRow.originalData?.id;
  //       return !isRecordMapped(rowId);
  //     });

  //     // Check if there are any new mappings to save
  //     if (newTargetRows.length === 0) {
  //       showError('All selected records are already mapped');
  //       setSavingMapping(false);
  //       return;
  //     }

  //     // Build payload for new mappings - send one at a time
  //     const payload = newTargetRows.map(targetRow => {
  //       if (isMappingFromGL) {
  //         // Mapping GL to Bank
  //         const glTransactionId = selectedSourceRow.originalData?.id; // ✅ GL is the source
  //         const bankStatementId = targetRow.originalData?.id;          // ✅ Bank is the target
  //         return {
  //           bankStatementId: bankStatementId,
  //           glTransactionId: glTransactionId,
  //           tenantId: tenantId,
  //         };
  //       } else {
  //         // Mapping Bank to GL
  //         const bankStatementId = selectedSourceRow.originalData?.id;
  //         const glTransactionId = targetRow.originalData?.id;
  //         return {
  //           bankStatementId: bankStatementId,
  //           glTransactionId: glTransactionId,
  //           tenantId: tenantId,
  //         };
  //       }
  //     });

  //     // Call new bulk API for all mappings at once
  //     const apiUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/recon-mapping-details/bulk`;

  //     const response = await fetch(apiUrl, {
  //       method: 'POST',
  //       headers: {
  //         'Authorization': `Bearer ${token}`,
  //         'Content-Type': 'application/json',
  //       },
  //       body: JSON.stringify(payload),
  //     });

  //     if (!response.ok) {
  //       const errorData = await response.json().catch(() => ({}));
  //       const errorMessage = errorData.error || errorData.message || `Failed to save mapping: ${response.status}`;
  //       throw new Error(errorMessage);
  //     }

  //     showSuccess('Mapping saved successfully');
  //     handleCloseMappingDialog();

  //     // Refresh the main grids
  //     fetchGLData(glPaginationModel.page, glPaginationModel.pageSize);
  //     fetchBankData(bankPaginationModel.page, bankPaginationModel.pageSize);

  //     // Refresh balance data after mapping save
  //     if (selectedProperty && selectedBankMonthYear) {
  //       const [year, month] = selectedBankMonthYear.split('-');
  //       const startDate = `${year}-${month}-01`;
  //       const lastDay = new Date(year, parseInt(month), 0).getDate();
  //       const endDate = `${year}-${month}-${lastDay.toString().padStart(2, '0')}`;

  //       const selectedProp = properties.find(p => p.PROPERTY_NAME === selectedProperty || p.propertyName === selectedProperty);
  //       const propertyId = selectedProp?.PROPERTY_ID || selectedProp?.propertyId || selectedProperty;

  //       fetchBalanceData(propertyId, startDate, endDate);
  //     }
  //   } catch (err) {
  //     showError(err.message || 'Failed to save mapping');
  //   } finally {
  //     setSavingMapping(false);
  //   }
  // };
  // Calculate match confidence dynamically based on amount and description similarity
  const calculateMatchConfidence = (sourceRow, targetRows) => {
    if (!sourceRow || targetRows.length === 0) return 0.5;

    const sourceAmount = Math.abs(sourceRow.debit || sourceRow.credit || 0);

    // Sum of all target amounts
    const targetAmount = targetRows.reduce((sum, row) => {
      return sum + Math.abs(row.debit || row.credit || 0);
    }, 0);

    if (sourceAmount === 0 || targetAmount === 0) return 0.5;

    // Amount similarity score (0 to 1)
    const amountDiff = Math.abs(sourceAmount - targetAmount);
    const amountScore = Math.max(0, 1 - amountDiff / sourceAmount);

    // Count score - 1:1 mapping is more confident than many-to-one
    const countScore = targetRows.length === 1 ? 1 : Math.max(0.5, 1 - (targetRows.length - 1) * 0.1);

    // Description similarity score
    const sourceDesc = (sourceRow.description || '').toLowerCase();
    const targetDescs = targetRows.map(r => (r.description || '').toLowerCase());
    const descScore = targetDescs.some(desc => {
      const sourceWords = sourceDesc.split(' ').filter(w => w.length > 3);
      return sourceWords.some(word => desc.includes(word));
    }) ? 1 : 0.7;

    // Weighted final score
    const confidence = (amountScore * 0.6) + (countScore * 0.2) + (descScore * 0.2);

    // Round to 2 decimal places, clamp between 0.5 and 1.0
    return Math.min(1.0, Math.max(0.5, Math.round(confidence * 100) / 100));
  };

  const handleSaveMapping = async () => {
    if (!canEdit || isReconReadOnly) {
      if (isReconReadOnly) {
        showError('This month is Historical (before go-live) and is view-only.');
      }
      return;
    }
    if (!selectedSourceRow || selectedTargetRows.length === 0) {
      showError('Please select at least one record to map');
      return;
    }

    setSavingMapping(true);

    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token || !tenantId) {
        throw new Error('Missing authentication or tenant information');
      }

      // Filter out already-mapped rows to avoid duplicates
      const newTargetRows = selectedTargetRows.filter(targetRow => {
        const rowId = targetRow.originalData?.id;
        return !isRecordMapped(rowId);
      });

      if (newTargetRows.length === 0) {
        showError('All selected records are already mapped');
        setSavingMapping(false);
        return;
      }

      // Get property ID
      const selectedProp = properties.find(p => p.PROPERTY_NAME === selectedProperty || p.propertyName === selectedProperty);
      const propertyId = selectedProp?.PROPERTY_ID || selectedProp?.propertyId || selectedProperty;

      const payload = newTargetRows.map(targetRow => {
        if (isMappingFromGL) {
          return {
            bankStatementId: targetRow.originalData?.id,
            glTransactionId: selectedSourceRow.originalData?.id,
            tenantId: tenantId,
          };
        } else {
          return {
            bankStatementId: selectedSourceRow.originalData?.id,
            glTransactionId: targetRow.originalData?.id,
            tenantId: tenantId,
          };
        }
      });

      // Step 1: Save the bulk mapping
      const apiUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/recon-mapping-details/bulk`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `Failed to save mapping: ${response.status}`);
      }

      const savedMappings = await response.json();

      // Extract the reconId from the first saved mapping (if available)
      const reconId = Array.isArray(savedMappings)
        ? savedMappings[0]?.id || savedMappings[0]?.reconId || null
        : savedMappings?.id || savedMappings?.reconId || null;

      // Collect IDs for bulk reconciliation calls
      const bankStatementIds = newTargetRows
        .filter(() => isMappingFromGL)
        .map(r => r.originalData?.id)
        .filter(Boolean);

      const glTransactionIds = newTargetRows
        .filter(() => !isMappingFromGL)
        .map(r => r.originalData?.id)
        .filter(Boolean);

      // If mapping from GL: source is GL, targets are Bank
      // If mapping from Bank: source is Bank, targets are GL
      const sourceBankIds = !isMappingFromGL
        ? [selectedSourceRow.originalData?.id].filter(Boolean)
        : [];
      const sourceGlIds = isMappingFromGL
        ? [selectedSourceRow.originalData?.id].filter(Boolean)
        : [];

      const allBankIds = [...sourceBankIds, ...bankStatementIds].filter(Boolean);
      const allGlIds = [...sourceGlIds, ...glTransactionIds].filter(Boolean);

      const reconPayloadBase = {
        tenantId: tenantId,
        propertyId: propertyId,
        reconStatus: 'MANUAL_MATCHED',
        matchedBy: 'USER',
        reconId: reconId,
        // matchConfidence: calculateMatchConfidence(selectedSourceRow, newTargetRows),
      };

      // Step 2: Bulk reconciliation for Bank statements
      if (allBankIds.length > 0) {
        try {
          const bankReconResp = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/bank-statements/bulk-reconciliation`,
            {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                ...reconPayloadBase,
                bankStatementIds: allBankIds,
              }),
            }
          );
          if (!bankReconResp.ok) {
            console.error('Bank bulk reconciliation failed:', await bankReconResp.text());
          }
        } catch (err) {
          console.error('Error in bank bulk reconciliation:', err);
        }
      }

      // Step 3: Bulk reconciliation for GL transactions
      if (allGlIds.length > 0) {
        try {
          const glReconResp = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/gl-transactions/bulk-reconciliation`,
            {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                ...reconPayloadBase,
                glTransactionIds: allGlIds,
              }),
            }
          );
          if (!glReconResp.ok) {
            console.error('GL bulk reconciliation failed:', await glReconResp.text());
          }
        } catch (err) {
          console.error('Error in GL bulk reconciliation:', err);
        }
      }

      showSuccess('Mapping saved successfully');
      handleCloseMappingDialog();

      // Refresh the main grids
      fetchGLData(glPaginationModel.page, glPaginationModel.pageSize);
      fetchBankData(bankPaginationModel.page, bankPaginationModel.pageSize);

      // Refresh balance data after mapping save
      if (selectedProperty && selectedBankMonthYear) {
        const [year, month] = selectedBankMonthYear.split('-');
        const startDate = `${year}-${month}-01`;
        const lastDay = new Date(year, parseInt(month), 0).getDate();
        const endDate = `${year}-${month}-${lastDay.toString().padStart(2, '0')}`;
        fetchBalanceData(propertyId, startDate, endDate);
      }
    } catch (err) {
      showError(err.message || 'Failed to save mapping');
    } finally {
      setSavingMapping(false);
    }
  };
  // Delete mapping function
  // const handleDeleteMapping = async (mappingId) => {
  //   setDeletingMapping(true);
  //   try {
  //     const token = localStorage.getItem('token');
  //     const tenantId = localStorage.getItem('tenant_id');

  //     if (!token || !tenantId) {
  //       throw new Error('Missing authentication or tenant information');
  //     }

  //     // Use soft delete API endpoint
  //     const apiUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/recon-mapping-details/soft/${mappingId}`;

  //     const response = await fetch(apiUrl, {
  //       method: 'DELETE',
  //       headers: {
  //         'Authorization': `Bearer ${token}`,
  //         'Content-Type': 'application/json',
  //       },
  //     });

  //     if (!response.ok) {
  //       const errorData = await response.json().catch(() => ({}));
  //       throw new Error(errorData.message || `Failed to unmap mapping: ${response.status}`);
  //     }

  //     showSuccess('Mapping unmapped successfully');
  //     setDeleteDialogOpen(false);
  //     setMappingToDelete(null);

  //     // Refresh existing mappings and data
  //     const targetRows = targetGridRows;
  //     await fetchExistingMappings(targetRows);

  //     // Refresh both main grids after unmapping
  //     fetchGLData(glPaginationModel.page, glPaginationModel.pageSize);
  //     fetchBankData(bankPaginationModel.page, bankPaginationModel.pageSize);

  //     // Refresh balance data after unmapping
  //     if (selectedProperty && selectedBankMonthYear) {
  //       const [year, month] = selectedBankMonthYear.split('-');
  //       const startDate = `${year}-${month}-01`;
  //       const lastDay = new Date(year, parseInt(month), 0).getDate();
  //       const endDate = `${year}-${month}-${lastDay.toString().padStart(2, '0')}`;

  //       const selectedProp = properties.find(p => p.PROPERTY_NAME === selectedProperty || p.propertyName === selectedProperty);
  //       const propertyId = selectedProp?.PROPERTY_ID || selectedProp?.propertyId || selectedProperty;

  //       fetchBalanceData(propertyId, startDate, endDate);
  //     }
  //   } catch (err) {
  //     showError(err.message || 'Failed to unmap mapping');
  //   } finally {
  //     setDeletingMapping(false);
  //   }
  // };
  const handleDeleteMapping = async (mappingId) => {
    if (!canDelete) return;
    setDeletingMapping(true);
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token || !tenantId) {
        throw new Error('Missing authentication or tenant information');
      }

      // Get the mapping object to extract GL and Bank IDs before deleting
      const mappingObject = existingMappings.find(m =>
        (m.id || m.bank_alias_id || m.cash_recon_bank_alias_id) === mappingId
      );

      // Step 1: Soft delete the mapping
      const apiUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/recon-mapping-details/soft/${mappingId}`;

      const response = await fetch(apiUrl, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to unmap mapping: ${response.status}`);
      }

      // Step 2: Update GL transaction reconciliation status to UNMATCHED
      if (mappingObject?.glTransactionId) {
        try {
          const glReconUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/gl-transactions/${mappingObject.glTransactionId}/reconciliation?recon_status=UNMATCHED`;
          const glReconResp = await fetch(glReconUrl, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          if (!glReconResp.ok) {
            console.error('Failed to update GL recon status:', await glReconResp.text());
          }
        } catch (err) {
          console.error('Error updating GL reconciliation status:', err);
        }
      }

      // Step 3: Update Bank statement reconciliation status to UNMATCHED
      if (mappingObject?.bankStatementId) {
        try {
          const bankReconUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/bank-statements/${mappingObject.bankStatementId}/reconciliation?recon_status=UNMATCHED`;
          const bankReconResp = await fetch(bankReconUrl, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          if (!bankReconResp.ok) {
            console.error('Failed to update Bank recon status:', await bankReconResp.text());
          }
        } catch (err) {
          console.error('Error updating Bank reconciliation status:', err);
        }
      }

      showSuccess('Mapping unmapped successfully');
      setDeleteDialogOpen(false);
      setMappingToDelete(null);

      // Refresh existing mappings and data
      const targetRows = targetGridRows;
      await fetchExistingMappings(targetRows);

      // Refresh both main grids after unmapping
      fetchGLData(glPaginationModel.page, glPaginationModel.pageSize);
      fetchBankData(bankPaginationModel.page, bankPaginationModel.pageSize);

      // Refresh balance data after unmapping
      if (selectedProperty && selectedBankMonthYear) {
        const [year, month] = selectedBankMonthYear.split('-');
        const startDate = `${year}-${month}-01`;
        const lastDay = new Date(year, parseInt(month), 0).getDate();
        const endDate = `${year}-${month}-${lastDay.toString().padStart(2, '0')}`;

        const selectedProp = properties.find(p => p.PROPERTY_NAME === selectedProperty || p.propertyName === selectedProperty);
        const propertyId = selectedProp?.PROPERTY_ID || selectedProp?.propertyId || selectedProperty;

        fetchBalanceData(propertyId, startDate, endDate);
      }
    } catch (err) {
      showError(err.message || 'Failed to unmap mapping');
    } finally {
      setDeletingMapping(false);
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirm = () => {
    if (!canDelete) return;
    if (mappingToDelete) {
      // Try different possible ID field names
      const mappingId = mappingToDelete.id || mappingToDelete.bank_alias_id || mappingToDelete.cash_recon_bank_alias_id;
      if (mappingId) {
        handleDeleteMapping(mappingId);
      } else {
        showError('Unable to find mapping ID');
      }
    }
  };

  // Delete all mappings function
  const handleDeleteAllMappings = async () => {
    if (!canDelete) return;
    setDeletingAllMappings(true);
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token || !tenantId) {
        throw new Error('Missing authentication or tenant information');
      }

      const apiUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/recon-mapping-details/soft-all-by-tenant?tenant_id=${tenantId}`;

      const response = await fetch(apiUrl, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to unmap all mappings: ${response.status}`);
      }

      // Check if the response has content before trying to parse as JSON
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.indexOf("application/json") !== -1) {
        const data = await response.json();
        const deletedCount = data.deleted_count || 0;
        showSuccess(data.message || `Successfully unmapped ${deletedCount} mapping(s)`);
      } else {
        // If no JSON content, assume success based on response.ok and show a generic success message
        showSuccess('Successfully unmapped all mappings');
      }

      setDeleteAllDialogOpen(false);

      // Refresh existing mappings after deletion
      await fetchExistingMappings(targetGridRows);

      // Refresh data after deletion
      fetchGLData(glPaginationModel.page, glPaginationModel.pageSize);
      fetchBankData(bankPaginationModel.page, bankPaginationModel.pageSize);

      // Refresh balance data with property and date filters
      if (selectedProperty && selectedBankMonthYear) {
        const [year, month] = selectedBankMonthYear.split('-');
        const startDate = `${year}-${month}-01`;
        const lastDay = new Date(year, parseInt(month), 0).getDate();
        const endDate = `${year}-${month}-${lastDay.toString().padStart(2, '0')}`;

        const selectedProp = properties.find(p => p.PROPERTY_NAME === selectedProperty || p.propertyName === selectedProperty);
        const propertyId = selectedProp?.PROPERTY_ID || selectedProp?.propertyId || selectedProperty;

        fetchBalanceData(propertyId, startDate, endDate);
      }
    } catch (err) {
      showError(err.message || 'Failed to unmap all mappings');
    } finally {
      setDeletingAllMappings(false);
    }
  };


  const glColumns = [
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 200,
      valueGetter: (params) => params.row.description || '',
    },
    {
      field: 'date',
      headerName: 'Date',
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => params.row.date || '',
    },
    {
      field: 'docNo',
      headerName: 'Doc-No',
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => params.row.docNo || '',
    },
    {
      field: 'debit',
      headerName: 'Debit',
      flex: 1,
      minWidth: 120,
      type: 'number',
      valueGetter: (params) => params.row.debit || 0,
      valueFormatter: (params) => {
        const value = params.value || 0;
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
        }).format(value);
      },
    },
    {
      field: 'credit',
      headerName: 'Credit',
      flex: 1,
      minWidth: 120,
      type: 'number',
      valueGetter: (params) => params.row.credit || 0,
      valueFormatter: (params) => {
        const value = params.value || 0;
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
        }).format(value);
      },
    },
    {
      field: 'integrationStatus',
      headerName: 'Integration Status',
      flex: 1,
      minWidth: 140,
      valueGetter: (params) => params.row.integrationStatus || params.row.originalData?.integrationStatus || '',
      renderCell: (params) => {
        const status = params.value || '';
        return (
          <Typography variant="body2" sx={{ color: status ? 'inherit' : '#999' }}>
            {status || 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: 'mappingDetails',
      headerName: 'Mapped To',
      flex: 1,
      minWidth: 260,
      renderCell: (params) => {
        const row = params.row;
        if (!row.originalData?.mappedBankIds || row.originalData.mappedBankIds.length === 0) {
          return <Typography variant="body2" sx={{ color: '#000000' }}>Not Mapped</Typography>;
        }

        return (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              alignItems: 'center',
              alignContent: 'flex-start',
              width: '100%',
            }}
          >
            {row.originalData.mappedBankIds.map((bankId, index) => {
              const bankDescription = getBankTransactionDescription(bankId);
              const mappedAmount = getMappedAmount(row.originalData.mappedBankAmounts, index);
              return (
                <Tooltip
                  key={bankId}
                  title={`${bankDescription}\nAmount: $${mappedAmount}\nBank ID: ${bankId}`}
                  arrow
                >
                  <Chip
                    label={`Bank ${index + 1}: $${mappedAmount}`}
                    size="small"
                    sx={{
                      backgroundColor: '#fff3e0',
                      color: '#e65100',
                      fontSize: '11px',
                      height: '20px',
                    }}
                  />
                </Tooltip>
              );
            })}
          </Box>
        );
      },
    },
    {
      field: 'map',
      headerName: 'Map',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Button
          size="small"
          variant="outlined"
          startIcon={<MapIcon />}
          onClick={(e) => {
            e.stopPropagation();
            handleOpenMappingDialog(params.row, true);
          }}
          sx={{
            color: ht.text,
            borderColor: ht.darkBlue,
            '&:hover': {
              borderColor: ht.darkBlue,
              backgroundColor: ht.secondaryTab,
            },
            textTransform: 'none',
          }}
        >
          Map
        </Button>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      sortable: false,
      renderCell: (params) => {
        const handleMenuClick = (event) => {
          event.stopPropagation();
          handleRowMenuClick(event, params.row);
        };

        return (
          <>
            <IconButton
              size="small"
              onClick={handleMenuClick}
              sx={{
                color: '#000000',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
              title="Actions"
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={rowMenuAnchorEl}
              open={Boolean(rowMenuAnchorEl) && selectedRowData?.id === params.row.id}
              onClose={handleRowMenuClose}
              PaperProps={{
                style: {
                  maxHeight: 48 * 4.5,
                  width: '22ch',
                },
              }}
            >
              {params.row.originalData?.isAdjustment && (
                <MenuItem
                  onClick={() => {
                    handleEditAdjustment(params.row);
                    handleRowMenuClose();
                  }}
                >
                  Edit
                </MenuItem>
              )}
              {/* <MenuItem
                onClick={() => {
                  handleReclassify(params.row.id);
                  handleRowMenuClose();
                }}
              >
                Reclassify
              </MenuItem> */}
              <MenuItem
                onClick={() => {
                  handleOpenPushToDateDialog(params.row);
                  handleRowMenuClose();
                }}
              >
                Push to another date
              </MenuItem>
            </Menu>
          </>
        );
      },
    },
  ];

  const bankColumns = [
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 200,
      valueGetter: (params) => params.row.description || '',
    },
    {
      field: 'date',
      headerName: 'Date',
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => params.row.date || '',
    },
    {
      field: 'docNo',
      headerName: 'Doc-No',
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => params.row.docNo || '',
    },
    {
      field: 'debit',
      headerName: 'Debit',
      flex: 1,
      minWidth: 120,
      type: 'number',
      valueGetter: (params) => params.row.debit || 0,
      valueFormatter: (params) => {
        const value = params.value || 0;
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
        }).format(value);
      },
    },
    {
      field: 'credit',
      headerName: 'Credit',
      flex: 1,
      minWidth: 120,
      type: 'number',
      valueGetter: (params) => params.row.credit || 0,
      valueFormatter: (params) => {
        const value = params.value || 0;
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
        }).format(value);
      },
    },
    {
      field: 'remarks',
      headerName: 'Remarks',
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => params.row.remarks || '',
    },
    {
      field: 'mappingDetails',
      headerName: 'Mapped To',
      flex: 1,
      minWidth: 260,
      renderCell: (params) => {
        const row = params.row;
        if (!row.originalData?.mappedGlIds || row.originalData.mappedGlIds.length === 0) {
          return <Typography variant="body2" sx={{ color: '#000000' }}>Not Mapped</Typography>;
        }

        return (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              alignItems: 'center',
              alignContent: 'flex-start',
              width: '100%',
            }}
          >
            {row.originalData.mappedGlIds.map((glId, index) => {
              const glDescription = getGLTransactionDescription(glId);
              const mappedAmount = getMappedAmount(row.originalData.mappedGlAmounts, index);
              return (
                <Tooltip
                  key={glId}
                  title={`${glDescription}\nAmount: $${mappedAmount}\nGL ID: ${glId}`}
                  arrow
                >
                  <Chip
                    label={`GL ${index + 1}: $${mappedAmount}`}
                    size="small"
                    sx={{
                      backgroundColor: '#fff3e0',
                      color: '#e65100',
                      fontSize: '11px',
                      height: '20px',
                    }}
                  />
                </Tooltip>
              );
            })}
          </Box>
        );
      },
    },
    {
      field: 'map',
      headerName: 'Map',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Button
          size="small"
          variant="outlined"
          startIcon={<MapIcon />}
          onClick={(e) => {
            e.stopPropagation();
            handleOpenMappingDialog(params.row, false);
          }}
          sx={{
            color: ht.text,
            borderColor: ht.darkBlue,
            '&:hover': {
              borderColor: ht.darkBlue,
              backgroundColor: ht.secondaryTab,
            },
            textTransform: 'none',
          }}
        >
          Map
        </Button>
      ),
    },
  ];

  // Show GL Reference page when selected
  if (showGLReference) {
    return (
      <GLReference
        onBack={() => setShowGLReference(false)}
      />
    );
  }

  // Show Bank Reference page when selected
  if (showBankReference) {
    return (
      <BankReference
        onBack={() => setShowBankReference(false)}
      />
    );
  }

  // Show Reclassification page when selected
  if (showReclassification) {
    return (
      <Reclassification
        onBack={() => setShowReclassification(false)}
      />
    );
  }

  return (
    <Box
      sx={{
        ...harmonatePageSx,
        p: 3,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexShrink: 0 }}>
        <Box>
          <Typography variant="h5" sx={harmonatePageTitleSx}>
            Cash Reconciliation
          </Typography>
          <Button
            size="small"
            onClick={() => navigate('/cash-recon')}
            sx={{ color: ht.white, textTransform: 'none', px: 0, mt: 0.5, minWidth: 0 }}
          >
            ← Status dashboard
          </Button>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: 300 }}>
            {/* <InputLabel>Hotel Select</InputLabel> */}
            <Autocomplete
                fullWidth
                size="small"
                options={properties}
                getOptionLabel={(option) => {
                  const code = option.PROPERTY_CODE || option.propertyCode || '';
                  const name = option.PROPERTY_NAME || option.propertyName || '';
                  return code && name ? `${code} - ${name}` : name;
                }}
                value={properties.find(p => (p.PROPERTY_NAME || p.propertyName) === selectedProperty) || null}
                onChange={(event, newValue) => {
                  const propertyValue = newValue ? (newValue.PROPERTY_NAME || newValue.propertyName) : '';
                  setSelectedProperty(propertyValue);
                  setSelectedBankMonthYear(''); // Reset month year when property changes
                  setGlDataLoaded(false); // Reset GL data loaded flag when property changes
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Hotel Select"
                    size="small"
                    placeholder="Select Hotel"
                    sx={{ minWidth: 300 }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {propertiesLoading ? (
                            <CircularProgress size={20} sx={{ color: ht.white }} />
                          ) : (
                            params.InputProps.endAdornment
                          )}
                        </>
                      )
                    }}
                  />
                )}
                isOptionEqualToValue={(option, value) => (option.PROPERTY_NAME || option.propertyName) === (value?.PROPERTY_NAME || value?.propertyName)}
                sx={{
                  ...harmonateToolbarBuFieldSx,
                  minWidth: 300,
                  '& .MuiAutocomplete-popupIndicator': { color: ht.white },
                  '& .MuiAutocomplete-clearIndicator': { color: 'rgba(255, 255, 255, 0.75)' },
                  '& .MuiAutocomplete-endAdornment': { right: 6 },
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'transparent',
                    borderRadius: '8px !important',
                    height: 40,
                    color: ht.white,
                    paddingRight: '36px !important',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.55)', borderRadius: '8px' },
                    '&:hover fieldset': { borderColor: ht.white },
                    '&.Mui-focused fieldset': { borderColor: ht.white },
                  },
                  '& .MuiOutlinedInput-input': { color: ht.white, fontSize: '0.875rem' },
                }}
                disabled={propertiesLoading}
              />
          </FormControl>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setRefreshDataOpen(true)}
            sx={harmonateToolbarButtonSx}
          >
            Refresh Data
          </Button>
          <IconButton
            onClick={(e) => setMenuAnchorEl(e.currentTarget)}
            sx={harmonatePlainIconButtonSx}
          >
            <MoreVertIcon />
          </IconButton>
        </Box>
        <Menu
          anchorEl={menuAnchorEl}
          open={menuOpen}
          onClose={() => setMenuAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem
            onClick={() => {
              setMenuAnchorEl(null);
              navigate('/cash-recon/account-mapping');
            }}
          >
            Account Selection
          </MenuItem>
          <MenuItem onClick={() => {
            setMenuAnchorEl(null);
            navigate('/cash-recon/compare');
          }}>
            Compare
          </MenuItem>
          <MenuItem onClick={() => {
            setMenuAnchorEl(null);
            navigate('/cash-recon/output');
          }}>
            Output
          </MenuItem>
          <MenuItem onClick={() => {
            setMenuAnchorEl(null);
            handleAddBank();
          }}>
            Add Bank
          </MenuItem>
          <MenuItem onClick={() => {
            setMenuAnchorEl(null);
            handleChartOfAccounts();
          }}>
            Chart of Accounts
          </MenuItem>
          <MenuItem onClick={() => {
            setMenuAnchorEl(null);
            handleGLTransactions();
          }}>
            GL Transaction
          </MenuItem>
          <MenuItem onClick={() => {
            setMenuAnchorEl(null);
            setBankStatementDrawerOpen(true);
          }}>
            Bank Statements
          </MenuItem>
          {/* <MenuItem onClick={() => {
            setMenuAnchorEl(null);
            setGlUploadDialogOpen(true);
          }}>
            GL Upload
          </MenuItem> */}
          {/* <MenuItem onClick={() => {
            setMenuAnchorEl(null);
            setBankUploadDialogOpen(true);
          }}>
            Bank Upload
          </MenuItem> */}
          {/* <MenuItem onClick={() => {
            setMenuAnchorEl(null);
            setUnifiedUploadDialogOpen(true);
          }}>
            Upload GL/Bank
          </MenuItem> */}
          {/* <MenuItem onClick={handleOpenGLReference}>
            GL Reference
          </MenuItem>
          <MenuItem onClick={handleOpenBankReference}>
            Bank Reference
          </MenuItem> */}
          <MenuItem onClick={handleOpenReclassification}>
            Reclassification
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMenuAnchorEl(null);
              navigate('/cash-recon');
            }}
          >
            Status Dashboard
          </MenuItem>
        </Menu>
      </Box>

      {reconGateMessage ? (
        <Alert
          severity={reconGateStatus === 'COMPLETED' ? 'info' : 'warning'}
          sx={{ mb: 2, flexShrink: 0 }}
          onClose={() => setReconGateMessage('')}
        >
          {reconGateMessage}
        </Alert>
      ) : null}

      {isHistoricalMonth ? (
        <Alert severity="info" sx={{ mb: 2, flexShrink: 0 }}>
          {selectedBankMonthYear} is before go-live ({propertyGoLiveMonth}) and is Historical — view only.
          Mapping, Auto Matching, Adjustments, and Approve are disabled. Select {propertyGoLiveMonth} or a later month to work.
        </Alert>
      ) : null}

      {/* Data Grids Section */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', gap: 2, mb: 2 }}>
        {/* Bank Grid */}
        <Paper
          elevation={0}
          sx={{
            ...harmonateCardSx,
            bgcolor: ht.white,
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              p: 1.5,
              bgcolor: ht.listBg,
              borderBottom: `1px solid ${ht.line}`,
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: ht.text }}>
                Bank
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, justifyContent: 'flex-end' }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Month & Year</InputLabel>
                  <Select
                    value={selectedBankMonthYear}
                    onChange={(e) => setSelectedBankMonthYear(e.target.value)}
                    disabled={bankMonthYearLoading || !selectedProperty || bankMonthYearOptions.length === 0}
                    label="Month & Year"
                    MenuProps={harmonateSelectMenuProps}
                  >
                    <MenuItem value="">Select Month & Year</MenuItem>
                    {bankMonthYearOptions.map((monthYear) => {
                      // Format month year for display (e.g., "2024-01" -> "January 2024")
                      const [year, month] = monthYear ? monthYear.split('-') : ['', ''];
                      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
                      const monthName = month ? monthNames[parseInt(month, 10) - 1] : '';
                      const isHist = propertyGoLiveMonth && monthYear < propertyGoLiveMonth;
                      const displayText = `${monthName} ${year}${isHist ? ' (Historical)' : ''}`;

                      return (
                        <MenuItem key={monthYear} value={monthYear}>
                          {displayText}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>State</InputLabel>
                  <Select
                    value={bankFilters.state}
                    onChange={(e) => setBankFilters({ ...bankFilters, state: e.target.value })}
                    label="State"
                    MenuProps={harmonateSelectMenuProps}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="mapped">Fully Mapped</MenuItem>
                    <MenuItem value="unmapped">Unmapped</MenuItem>
                    <MenuItem value="partially_mapped">Partially Mapped</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleLoadBankData}
                  disabled={bankLoading || !selectedProperty || !selectedBankMonthYear || bankMonthYearOptions.length === 0}
                  sx={harmonateProceedButtonSx}
                >
                  Load
                </Button>
              </Box>
            </Box>
            {/* Reserve caption row height so Bank/GL grids align when only one side shows a label */}
            <Box sx={{ minHeight: '20px', display: 'block' }}>
              {selectedBankMonthYear && bankAccountNoForSelectedMonth ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Bank account: {bankAccountNoForSelectedMonth}
                </Typography>
              ) : null}
            </Box>
          </Box>
          {bankError && (
            <Alert severity="error" sx={{ m: 1, flexShrink: 0 }} onClose={() => setBankError(null)}>
              {bankError}
            </Alert>
          )}
          <DataGridPro
            rows={bankRows}
            columns={bankColumns}
            getRowHeight={() => 'auto'}
            sx={{
              ...harmonateDataGridSx,
              flex: 1,
              minHeight: 0,
              '& .MuiDataGrid-cell[data-field="mappingDetails"]': {
                whiteSpace: 'normal',
                lineHeight: 1.25,
                alignItems: 'flex-start',
                display: 'flex',
                py: 0.75,
              },
              '& .MuiDataGrid-row:hover': { bgcolor: ht.secondaryTab },
              '& .MuiDataGrid-row.fully-mapped-row': {
                bgcolor: '#e8f5e9',
                '&:hover': { bgcolor: '#c8e6c9' },
              },
              '& .MuiDataGrid-row.partially-mapped-row': {
                bgcolor: 'rgba(7, 204, 37, 0.55)',
                '&:hover': { bgcolor: 'rgba(10, 220, 41, 0.55)' },
              },
              // Ensure selected rows have distinct color
              '& .MuiDataGrid-row.Mui-selected:not(.fully-mapped-row):not(.partially-mapped-row)': {
                bgcolor: '#f5f5f5 !important',  // Grey for selection
                '&:hover': { bgcolor: '#e0e0e0 !important' },  // Darker grey on hover
                border: '1px solid #bdbdbd',  // Grey border
              },
            }}
            disableSelectionOnClick
            autoHeight={false}
            loading={bankLoading}
            pagination
            paginationMode="client"
            paginationModel={bankPaginationModel}
            onPaginationModelChange={setBankPaginationModel}
            pageSizeOptions={[10, 25, 50, 100]}
            getRowId={(row) => row.id}
            getRowClassName={(params) => {
              if (params.row.mappingStatus === 'fully_mapped') {
                return 'fully-mapped-row';
              } else if (params.row.mappingStatus === 'partially_mapped') {
                return 'partially-mapped-row';
              }
              return '';
            }}
          />
        </Paper>

        {/* GL Grid */}
        <Paper
          elevation={0}
          sx={{
            ...harmonateCardSx,
            bgcolor: ht.white,
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              p: 1.5,
              bgcolor: ht.listBg,
              borderBottom: `1px solid ${ht.line}`,
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: ht.text }}>
                GL
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                size="small"
                label="From Date"
                type="date"
                value={glFilters.fromDate}
                onChange={(e) => setGlFilters({ ...glFilters, fromDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 130, ...harmonateFormFieldsSx }}
              />
              <TextField
                size="small"
                label="To Date"
                type="date"
                value={glFilters.toDate}
                onChange={(e) => setGlFilters({ ...glFilters, toDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 130, ...harmonateFormFieldsSx }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>State</InputLabel>
                <Select
                  value={glFilters.state}
                  onChange={(e) => setGlFilters({ ...glFilters, state: e.target.value })}
                  label="State"
                  MenuProps={harmonateSelectMenuProps}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="mapped">Fully Mapped</MenuItem>
                  <MenuItem value="unmapped">Unmapped</MenuItem>
                  <MenuItem value="partially_mapped">Partially Mapped</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                size="small"
                onClick={handleLoadGLData}
                disabled={glLoading || !selectedProperty || !glFilters.fromDate || !glFilters.toDate}
                sx={harmonateProceedButtonSx}
              >
                Load
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => setBulkReclassifyDialogOpen(true)}
                disabled={selectedGlRows.length === 0 || glLoading}
                sx={{ ...harmonateUploadOutlinedSx, ml: 1 }}
              >
                Reclassify ({selectedGlRows.length})
              </Button>
              </Box>
            </Box>
            <Box sx={{ minHeight: '20px', display: 'block' }}>
              {(currentMappingHeaderForGl?.glAccountName || currentMappingHeaderForGl?.glAccountNo) ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  GL Account:{' '}
                  {currentMappingHeaderForGl.glAccountNo && currentMappingHeaderForGl.glAccountName
                    ? `${currentMappingHeaderForGl.glAccountNo} - ${currentMappingHeaderForGl.glAccountName}`
                    : currentMappingHeaderForGl.glAccountNo || currentMappingHeaderForGl.glAccountName}
                </Typography>
              ) : null}
            </Box>
          </Box>
          {glError && (
            <Alert severity="error" sx={{ m: 1, flexShrink: 0 }} onClose={() => setGlError(null)}>
              {glError}
            </Alert>
          )}
          <DataGridPro
            rows={glRows}
            columns={glColumns}
            getRowHeight={() => 'auto'}
            checkboxSelection={true}
            onRowSelectionModelChange={(newSelection) => {
              setSelectedGlRows(newSelection);
            }}
            rowSelectionModel={selectedGlRows}
            onRowClick={(params) => {
              setSelectedGlRow(params.row);
              setGlDrawerOpen(true);
              setGlComment(''); // Reset comment when opening drawer
            }}
            sx={{
              ...harmonateDataGridSx,
              flex: 1,
              minHeight: 0,
              '& .MuiDataGrid-cell[data-field="mappingDetails"]': {
                whiteSpace: 'normal',
                lineHeight: 1.25,
                alignItems: 'flex-start',
                display: 'flex',
                py: 0.75,
              },
              '& .MuiDataGrid-row:hover': { bgcolor: ht.secondaryTab, cursor: 'pointer' },
              '& .MuiDataGrid-row.fully-mapped-row': {
                bgcolor: '#e8f5e9',
                '&:hover': { bgcolor: '#c8e6c9' },
              },
              '& .MuiDataGrid-row.partially-mapped-row': {
                bgcolor: 'rgba(0, 212, 31, 0.16)',
                '&:hover': { bgcolor: 'rgba(0, 212, 31, 0.28)' },
              },
              // Ensure selected rows have distinct color
              '& .MuiDataGrid-row.Mui-selected:not(.fully-mapped-row):not(.partially-mapped-row)': {
                bgcolor: '#f5f5f5 !important',  // Grey for selection
                '&:hover': { bgcolor: '#e0e0e0 !important' },  // Darker grey on hover
                border: '1px solid #bdbdbd',  // Grey border
              },
            }}
            disableSelectionOnClick={false}
            autoHeight={false}
            loading={glLoading}
            pagination
            paginationMode="server"
            rowCount={glRowCount}
            paginationModel={glPaginationModel}
            onPaginationModelChange={(model) => {
              if (model.page === glPaginationModel.page && model.pageSize === glPaginationModel.pageSize) {
                return;
              }
              setGlPaginationModel(model);
              if (!glDataLoaded) return;
              fetchGLData(model.page, model.pageSize);
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            getRowId={(row) => row.id}
            getRowClassName={(params) => {
              if (params.row.mappingStatus === 'fully_mapped') {
                return 'fully-mapped-row';
              } else if (params.row.mappingStatus === 'partially_mapped') {
                return 'partially-mapped-row';
              }
              return '';
            }}
          />
        </Paper>
      </Box>

      {/* Footer Section */}
      <Paper
        elevation={0}
        sx={{ ...harmonateCardSx, p: 2, bgcolor: ht.white, flexShrink: 0 }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          {/* Left Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {/* <Button
              variant="contained"
              sx={{
                bgcolor: ht.green,
                '&:hover': { bgcolor: ht.green },
                textTransform: 'none',
              }}
            >
              Adjust Transaction
            </Button> */}
            {/* <Button
              variant="contained"
              onClick={() => setDeleteAllDialogOpen(true)}
              disabled={deletingAllMappings}
              sx={{
                bgcolor: ht.red,
                '&:hover': { bgcolor: ht.red },
                textTransform: 'none',
              }}
            >
              Unmap All
            </Button> */}
            <Button
              variant="contained"
              // size="small"
              onClick={handleAutoMatchingDialogOpen}
              disabled={bankRows.length === 0 || glRows.length === 0 || isReconReadOnly}
              startIcon={<AutoAwesomeIcon />}
              sx={harmonateProceedButtonSx}
            >
              Auto Matching
            </Button>
          </Box>

          {/* Reconciliation Summary */}
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Bank Total
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, color: ht.text }}>
                {balanceLoading ? (
                  <CircularProgress size={16} sx={{ color: ht.green }} />
                ) : (
                  new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 2,
                  }).format(balanceData.bankBalance)
                )}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                GL Total
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, color: ht.text }}>
                {balanceLoading ? (
                  <CircularProgress size={16} sx={{ color: ht.green }} />
                ) : (
                  new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 2,
                  }).format(balanceData.glBalance)
                )}
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="body2" color="text.secondary">
                Delta
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: balanceData.delta < 0 ? ht.red : ht.green
                }}
              >
                {balanceLoading ? (
                  <CircularProgress size={16} sx={{ color: ht.green }} />
                ) : (
                  new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 2,
                    signDisplay: 'always',
                  }).format(balanceData.delta)
                )}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Ending Bank Account Balance
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, color: ht.text }}>
                {balanceLoading ? (
                  <CircularProgress size={16} sx={{ color: ht.green }} />
                ) : (
                  new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 2,
                  }).format(balanceData.endingBankBalance)
                )}
              </Typography>
            </Box>
          </Box>

          {/* Right Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Tooltip title={isReconReadOnly ? 'Historical month is view-only' : (canAdd ? 'Add Adjustment' : 'No add permission')}>
              <span>
                <Button
                  variant="contained"
                  onClick={() => {
                    if (!canAdd || isReconReadOnly) return;
                    setAdjustmentDialogOpen(true);
                  }}
                  disabled={!selectedProperty || selectedProperty === '' || glLoading || !glDataLoaded || !canAdd || isReconReadOnly}
                  sx={{ ...harmonateUploadOutlinedSx, mr: 1 }}
                >
                  Add Adjustment
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={isReconReadOnly ? 'Historical month is view-only' : (canEdit ? 'Approve' : 'No edit permission')}>
              <span>
                <Button
                  variant="contained"
                  onClick={handleApproveClick}
                  disabled={!headerId || !canEdit || isReconReadOnly}
                  sx={harmonateProceedButtonSx}
                >
                  Approve
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* Mapping Dialog */}
      <Dialog
        open={mappingDialogOpen}
        onClose={handleCloseMappingDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            ...harmonateDialogPaperProps.sx,
            height: '90vh',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Map {isMappingFromGL ? 'GL to Bank' : 'Bank to GL'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                variant={enableDeleteMode ? 'contained' : 'outlined'}
                size="small"
                onClick={() => {
                  if (!canDelete) return;
                  setEnableDeleteMode(!enableDeleteMode);
                }}
                disabled={existingMappings.length === 0 || !canDelete}
                sx={enableDeleteMode ? harmonateAbortButtonSx : harmonateCancelButtonSx}
              >
                {enableDeleteMode ? 'Disable Unmap' : 'Enable Unmap'}
              </Button>
              <IconButton
                onClick={handleCloseMappingDialog}
                size="small"
                sx={harmonatePlainIconButtonSx}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx, overflow: 'hidden' }}>
          {/* Source Row Details and Calculation */}
          {selectedSourceRow && (() => {
            const totals = calculateMappingTotals();
            return (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: ht.listBg,
                  borderRadius: ht.cardRadius,
                  border: `1px solid ${ht.line}`,
                }}
              >
                <Grid container spacing={3}>
                  {/* Source Record Details */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#000000' }}>
                      {isMappingFromGL ? 'GL Record Details' : 'Bank Record Details'}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="body2" color="text.secondary">Description</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {selectedSourceRow.description || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">Date</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {selectedSourceRow.date || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3} md={2.5}>
                        <Typography variant="body2" color="text.secondary">Debit</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 2,
                          }).format(selectedSourceRow.debit || 0)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3} md={2.5}>
                        <Typography variant="body2" color="text.secondary">Credit</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 2,
                          }).format(selectedSourceRow.credit || 0)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Calculation Section - Horizontal Layout */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#000000' }}>
                      Calculation
                    </Typography>
                    <Grid container spacing={3}>
                      {/* Source Value (Debit or Credit based on what has value) */}
                      {Math.abs(totals.sourceDebit) > 0 && Math.abs(totals.sourceCredit) === 0 && (
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {isMappingFromGL ? 'GL Debit' : 'Bank Debit'}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: ht.text }}>
                            {new Intl.NumberFormat('en-US', {
                              style: 'currency',
                              currency: 'USD',
                              minimumFractionDigits: 2,
                            }).format(Math.abs(totals.sourceDebit))}
                          </Typography>
                        </Grid>
                      )}
                      {Math.abs(totals.sourceCredit) > 0 && Math.abs(totals.sourceDebit) === 0 && (
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {isMappingFromGL ? 'GL Credit' : 'Bank Credit'}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: ht.text }}>
                            {new Intl.NumberFormat('en-US', {
                              style: 'currency',
                              currency: 'USD',
                              minimumFractionDigits: 2,
                            }).format(Math.abs(totals.sourceCredit))}
                          </Typography>
                        </Grid>
                      )}

                      {/* Target Sum (Credit or Debit based on mapping direction) */}
                      {Math.abs(totals.sourceDebit) > 0 && Math.abs(totals.sourceCredit) === 0 && (
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {isMappingFromGL ? 'Bank Credit (Selected)' : 'GL Credit (Selected)'}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: ht.text }}>
                            {new Intl.NumberFormat('en-US', {
                              style: 'currency',
                              currency: 'USD',
                              minimumFractionDigits: 2,
                            }).format(Math.abs(totals.targetCredit))}
                          </Typography>
                        </Grid>
                      )}
                      {Math.abs(totals.sourceCredit) > 0 && Math.abs(totals.sourceDebit) === 0 && (
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {isMappingFromGL ? 'Bank Debit (Selected)' : 'GL Debit (Selected)'}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: ht.text }}>
                            {new Intl.NumberFormat('en-US', {
                              style: 'currency',
                              currency: 'USD',
                              minimumFractionDigits: 2,
                            }).format(Math.abs(totals.targetDebit))}
                          </Typography>
                        </Grid>
                      )}

                      {/* Delta */}
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          Delta
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            color: Math.abs(totals.delta) < 0.01 ? ht.green : ht.red
                          }}
                        >
                          {new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 2,
                            signDisplay: 'always',
                          }).format(totals.delta)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Paper>
            );
          })()}

          {/* Search Bar */}
          <Box>
            <TextField
              fullWidth
              size="small"
              placeholder={`Search ${isMappingFromGL ? 'Bank' : 'GL'} records...`}
              value={targetGridSearch}
              onChange={(e) => {
                setTargetGridSearch(e.target.value);
                setTargetGridPaginationModel(prev => ({ page: 0, pageSize: prev.pageSize }));
              }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
              }}
            />
          </Box>

          {/* Target Grid */}
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              minHeight: 0,
              borderRadius: ht.cardRadius,
              overflow: 'hidden',
              border: `1px solid ${ht.line}`,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <DataGrid
              rows={targetGridRows}
              columns={[
                {
                  field: 'select',
                  headerName: '',
                  width: 50,
                  sortable: false,
                  renderCell: (params) => {
                    // Check if this row is already mapped
                    const rowId = params.row.originalData?.id;
                    const isAlreadyMapped = isRecordMapped(rowId);
                    const mappingObject = getMappingForRecord(rowId);

                    // Validate debit/credit matching
                    let isValidForSelection = true;
                    if (selectedSourceRow) {
                      const sourceDebit = selectedSourceRow.debit || 0;
                      const sourceCredit = selectedSourceRow.credit || 0;
                      const targetDebit = params.row.debit || 0;
                      const targetCredit = params.row.credit || 0;

                      // Check if source has non-zero debit (positive or negative) and credit is 0
                      // Or if source has non-zero credit (positive or negative) and debit is 0
                      const hasSourceDebit = Math.abs(sourceDebit) > 0;
                      const hasSourceCredit = Math.abs(sourceCredit) > 0;
                      const hasTargetDebit = Math.abs(targetDebit) > 0;
                      const hasTargetCredit = Math.abs(targetCredit) > 0;

                      // If source has debit (and credit is 0), target must have credit (and debit is 0)
                      // If source has credit (and debit is 0), target must have debit (and credit is 0)
                      if (hasSourceDebit && Math.abs(sourceCredit) === 0) {
                        // Source is debit transaction - target must be credit transaction
                        isValidForSelection = hasTargetCredit && Math.abs(targetDebit) === 0;
                      } else if (hasSourceCredit && Math.abs(sourceDebit) === 0) {
                        // Source is credit transaction - target must be debit transaction
                        isValidForSelection = hasTargetDebit && Math.abs(targetCredit) === 0;
                      }
                    }

                    const isDisabled = isAlreadyMapped || !isValidForSelection;
                    const isChecked = selectedTargetRows.some(r => r.id === params.row.id) || isAlreadyMapped;

                    return (
                      <Checkbox
                        checked={isChecked}
                        disabled={isDisabled}
                        onChange={(e) => {
                          if (e.target.checked && isValidForSelection) {
                            setSelectedTargetRows(prev => [...prev, params.row]);
                          } else {
                            setSelectedTargetRows(prev => prev.filter(r => r.id !== params.row.id));
                          }
                        }}
                        size="small"
                        sx={{
                          color: ht.green,
                          '&.Mui-checked': {
                            color: ht.green,
                          },
                          '&.Mui-disabled': {
                            color: ht.green,
                            opacity: 0.6,
                          },
                        }}
                      />
                    );
                  },
                },
                // Add delete column conditionally when delete mode is enabled
                ...(enableDeleteMode ? [{
                  field: 'delete',
                  headerName: '',
                  width: 60,
                  sortable: false,
                  renderCell: (params) => {
                    const rowId = params.row.originalData?.id;
                    const isAlreadyMapped = isRecordMapped(rowId);
                    const mappingObject = getMappingForRecord(rowId);

                    if (!isAlreadyMapped || !mappingObject) {
                      return null;
                    }

                    return (
                      <IconButton
                        size="small"
                        onClick={() => {
                          setMappingToDelete(mappingObject);
                          setDeleteDialogOpen(true);
                        }}
                        sx={{
                          color: ht.red,
                          '&:hover': {
                            backgroundColor: 'rgba(244, 67, 54, 0.08)',
                          },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    );
                  },
                }] : []),
                {
                  field: 'description',
                  headerName: 'Description',
                  flex: 1,
                  minWidth: 200,
                },
                {
                  field: 'date',
                  headerName: 'Date',
                  width: 120,
                },
                {
                  field: 'docNo',
                  headerName: 'Doc-No',
                  width: 150,
                  minWidth: 120,
                },
                {
                  field: 'debit',
                  headerName: 'Debit',
                  width: 120,
                  type: 'number',
                  valueFormatter: (params) => {
                    const value = params.value || 0;
                    return new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD',
                      minimumFractionDigits: 2,
                    }).format(value);
                  },
                },
                {
                  field: 'credit',
                  headerName: 'Credit',
                  width: 120,
                  type: 'number',
                  valueFormatter: (params) => {
                    const value = params.value || 0;
                    return new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD',
                      minimumFractionDigits: 2,
                    }).format(value);
                  },
                },
                ...(isMappingFromGL ? [{
                  field: 'remarks',
                  headerName: 'Remarks',
                  width: 200,
                  minWidth: 150,
                  flex: 1,
                }] : []),
              ]}
              rowHeight={40}
              sx={{
              ...harmonateDataGridSx,
                flex: 1,
                minHeight: 0,
              '& .MuiDataGrid-row:hover': { bgcolor: ht.secondaryTab },
                '& .MuiDataGrid-row.invalid-selection': {
                  opacity: 0.4,
                  backgroundColor: '#fafafa',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                },
              }}
              getRowClassName={(params) => {
                // Check if row is valid for selection based on debit/credit matching
                if (selectedSourceRow) {
                  const sourceDebit = selectedSourceRow.debit || 0;
                  const sourceCredit = selectedSourceRow.credit || 0;
                  const targetDebit = params.row.debit || 0;
                  const targetCredit = params.row.credit || 0;

                  // Check if source has non-zero debit (positive or negative) and credit is 0
                  // Or if source has non-zero credit (positive or negative) and debit is 0
                  const hasSourceDebit = Math.abs(sourceDebit) > 0;
                  const hasSourceCredit = Math.abs(sourceCredit) > 0;
                  const hasTargetDebit = Math.abs(targetDebit) > 0;
                  const hasTargetCredit = Math.abs(targetCredit) > 0;

                  let isValidForSelection = true;
                  if (hasSourceDebit && Math.abs(sourceCredit) === 0) {
                    // Source is debit transaction - target must be credit transaction
                    isValidForSelection = hasTargetCredit && Math.abs(targetDebit) === 0;
                  } else if (hasSourceCredit && Math.abs(sourceDebit) === 0) {
                    // Source is credit transaction - target must be debit transaction
                    isValidForSelection = hasTargetDebit && Math.abs(targetCredit) === 0;
                  }

                  if (!isValidForSelection) {
                    return 'invalid-selection';
                  }
                }
                return '';
              }}
              disableSelectionOnClick
              autoHeight={false}
              loading={targetGridLoading}
              pagination
              paginationMode={isMappingFromGL ? 'client' : 'server'}
              rowCount={isMappingFromGL ? targetGridRows.length : targetGridRowCount}
              paginationModel={targetGridPaginationModel}
              onPaginationModelChange={(model) => {
                if (model.page === targetGridPaginationModel.page && model.pageSize === targetGridPaginationModel.pageSize) {
                  return;
                }
                setTargetGridPaginationModel(model);
                if (!isMappingFromGL && mappingDialogOpen) {
                  fetchTargetGridData(model.page, model.pageSize, true, targetGridSearch);
                }
              }}
              pageSizeOptions={[10, 25, 50, 100]}
              getRowId={(row) => row.id}
              checkboxSelection={false}
            />
          </Paper>

          {/* Selected Count */}
          {selectedTargetRows.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              {selectedTargetRows.length} record(s) selected
            </Typography>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={handleCloseMappingDialog}
            variant="outlined"
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveMapping}
            variant="contained"
            disabled={selectedTargetRows.length === 0 || savingMapping || !canEdit}
            startIcon={savingMapping ? <CircularProgress size={16} /> : <SaveIcon />}
            sx={harmonateProceedButtonSx}
          >
            {savingMapping ? 'Saving...' : 'Save Mapping'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setMappingToDelete(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>Unmap Mapping</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography>
            Are you sure you want to unmap this mapping? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setMappingToDelete(null);
            }}
            variant="outlined"
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            disabled={deletingMapping || !canDelete}
            startIcon={deletingMapping ? <CircularProgress size={16} /> : <DeleteIcon />}
            sx={harmonateAbortButtonSx}
          >
            {deletingMapping ? 'Unmapping...' : 'Unmap'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete All Mappings Confirmation Dialog */}
      <Dialog
        open={deleteAllDialogOpen}
        onClose={() => {
          setDeleteAllDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>Unmap All Mappings</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography>
            Are you sure you want to Unmap all mappings? This action will permanently Unmap all existing mappings and cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={() => {
              setDeleteAllDialogOpen(false);
            }}
            variant="outlined"
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAllMappings}
            variant="contained"
            disabled={deletingAllMappings}
            startIcon={deletingAllMappings ? <CircularProgress size={16} /> : <DeleteIcon />}
            sx={harmonateAbortButtonSx}
          >
            {deletingAllMappings ? 'Unmapping...' : 'Unmap All'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Auto Matching Dialog */}
      <Dialog
        open={autoMatchingDialogOpen}
        onClose={() => !autoMatchingLoading && setAutoMatchingDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            ...harmonateDialogPaperProps.sx,
            height: '90vh',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Auto Matching
            </Typography>
            <IconButton
              onClick={() => {
                setAutoMatchingDialogOpen(false);
                setAutoMatchedPairs([]);
              }}
              size="small"
              disabled={autoMatchingLoading}
              sx={harmonatePlainIconButtonSx}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ ...harmonateDialogFormContentSx, overflow: 'hidden' }}>
          {autoMatchingLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {autoMatchedPairs.length === 0
                  ? 'No matching records found based on the criteria'
                  : `Found ${autoMatchedPairs.length} matching pair(s)`}
              </Typography>
              <Paper
                elevation={0}
                sx={{
                  flex: 1,
                  minHeight: 0,
                  borderRadius: ht.cardRadius,
                  overflow: 'hidden',
                  boxShadow: '0px 4px 12px rgba(0,0,0,0.15)',
                  border: `1px solid ${ht.line}`,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <DataGridPro
                  rows={autoMatchedPairs.map((pair, index) => ({
                    id: index,
                    bankDate: pair.bankRow.date || 'N/A',
                    bankDescription: pair.bankDescription || 'N/A',
                    bankDebit: pair.bankRow.debit || 0,
                    bankCredit: pair.bankRow.credit || 0,
                    bankAmount: pair.bankAmount,
                    bankDocNo: pair.bankDocNo || 'N/A',
                    glDate: pair.glRow.date || 'N/A',
                    glDescription: pair.glDescription || 'N/A',
                    glDebit: pair.glRow.debit || 0,
                    glCredit: pair.glRow.credit || 0,
                    glAmount: pair.glAmount,
                    glDocNo: pair.glDocNo || 'N/A',
                    originalPair: pair,
                  }))}
                  columns={[
                    {
                      field: 'bankDate',
                      headerName: 'Bank Date',
                      width: 120,
                    },
                    {
                      field: 'bankDescription',
                      headerName: 'Bank Description',
                      flex: 1,
                      minWidth: 200,
                    },
                    {
                      field: 'bankDebit',
                      headerName: 'Bank Debit',
                      width: 120,
                      align: 'right',
                      type: 'number',
                      valueFormatter: (params) => {
                        const value = params.value || 0;
                        return new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 2,
                        }).format(value);
                      },
                    },
                    {
                      field: 'bankCredit',
                      headerName: 'Bank Credit',
                      width: 120,
                      align: 'right',
                      type: 'number',
                      valueFormatter: (params) => {
                        const value = params.value || 0;
                        return new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 2,
                        }).format(value);
                      },
                    },
                    {
                      field: 'bankDocNo',
                      headerName: 'Bank Document Number',
                      width: 180,
                      minWidth: 150,
                    },
                    {
                      field: 'glDate',
                      headerName: 'GL Date',
                      width: 120,
                    },
                    {
                      field: 'glDescription',
                      headerName: 'GL Description',
                      flex: 1,
                      minWidth: 200,
                    },
                    {
                      field: 'glDebit',
                      headerName: 'GL Debit',
                      width: 120,
                      align: 'right',
                      type: 'number',
                      valueFormatter: (params) => {
                        const value = params.value || 0;
                        return new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 2,
                        }).format(value);
                      },
                    },
                    {
                      field: 'glCredit',
                      headerName: 'GL Credit',
                      width: 120,
                      align: 'right',
                      type: 'number',
                      valueFormatter: (params) => {
                        const value = params.value || 0;
                        return new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 2,
                        }).format(value);
                      },
                    },
                    {
                      field: 'glDocNo',
                      headerName: 'GL Document Number',
                      width: 180,
                      minWidth: 150,
                    },
                  ]}
                  rowHeight={40}
                  pagination
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  pageSize={10}
                  autoPageSize={false}
                  sx={{
                    ...harmonateDataGridSx,
                    flex: 1,
                    bgcolor: '#ffffff',
                    '& .MuiDataGrid-row': {
                      borderBottom: '1px solid #e0e0e0',
                      '&:hover': { bgcolor: ht.secondaryTab },
                    },
                    '& .MuiDataGrid-cell': {
                      border: 'none',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      bgcolor: ht.listBg,
                      borderBottom: `2px solid ${ht.line}`,
                      fontWeight: 600,
                    },
                    '& .MuiDataGrid-columnHeader': {
                      border: 'none',
                    },
                    '& .MuiDataGrid-columnSeparator': {
                      display: 'none',
                    },
                  }}
                  disableSelectionOnClick
                  getRowId={(row) => row.id}
                />
              </Paper>
            </>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={() => {
              setAutoMatchingDialogOpen(false);
              setAutoMatchedPairs([]);
            }}
            variant="outlined"
            disabled={autoMatchingLoading}
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveAutoMatches}
            variant="contained"
            disabled={autoMatchingLoading || autoMatchedPairs.length === 0}
            startIcon={autoMatchingLoading ? <CircularProgress size={16} /> : <SaveIcon />}
            sx={harmonateProceedButtonSx}
          >
            {autoMatchingLoading ? 'Saving...' : `Save ${autoMatchedPairs.length} Match(es)`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve Confirmation Dialog */}
      <Dialog
        open={approveDialogOpen}
        onClose={() => !approving && setApproveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Approve Reconciliation
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
          <Typography variant="body2" color="text.secondary">
            Please review and confirm the ending bank account balance before approving the reconciliation.
          </Typography>

          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Current Ending Bank Account Balance
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, color: ht.text, mb: 2 }}>
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 2,
              }).format(balanceData.endingBankBalance || 0)}
            </Typography>
          </Box>

          <TextField
            fullWidth
            label="Ending Bank Account Balance"
            type="number"
            value={editableEndingBalance}
            onChange={(e) => {
              const value = parseFloat(e.target.value) || 0;
              setEditableEndingBalance(value);
            }}
            InputProps={{
              startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>$</Typography>,
            }}
            inputProps={{
              step: '0.01',
              min: 0,
            }}
            helperText="Edit the ending bank account balance if needed"
            sx={{ mt: 1 }}
          />

          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 500 }}>
              Approval Scope
            </Typography>
            <FormControl component="fieldset">
              <RadioGroup
                value={approvalScope}
                onChange={(e) => setApprovalScope(e.target.value)}
                sx={{ gap: 1 }}
              >
                <FormControlLabel
                  value="selected_period"
                  control={<Radio size="small" />}
                  label="Selected Period"
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: '0.875rem',
                      color: 'text.primary'
                    }
                  }}
                />
                <FormControlLabel
                  value="mapped_records"
                  control={<Radio size="small" />}
                  label="Mapped Records"
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: '0.875rem',
                      color: 'text.primary'
                    }
                  }}
                />
              </RadioGroup>
            </FormControl>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={() => setApproveDialogOpen(false)}
            variant="outlined"
            disabled={approving}
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={updateMappingHeader}
            variant="contained"
            disabled={approving || !canEdit}
            startIcon={approving ? <CircularProgress size={16} /> : null}
            sx={harmonateProceedButtonSx}
          >
            {approving ? 'Approving...' : 'Confirm & Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* GL Side Drawer */}
      <Drawer
        anchor="right"
        open={glDrawerOpen}
        onClose={() => {
          setGlDrawerOpen(false);
          setSelectedGlRow(null);
          setGlComment('');
        }}
        PaperProps={{
          sx: {
            width: 400,
            maxWidth: '90vw',
            bgcolor: ht.drawerBg,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box sx={harmonateDrawerHeaderFlushSx}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            GL Record Details
          </Typography>
          <IconButton
            onClick={() => {
              setGlDrawerOpen(false);
              setSelectedGlRow(null);
              setGlComment('');
            }}
            size="small"
            sx={harmonateDialogCloseIconSx}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={harmonateDrawerBodySx}>
          {/* Selected Row Details */}
          {selectedGlRow && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#000000' }}>
                Transaction Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Description</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedGlRow.description || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Date</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedGlRow.date || 'N/A'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Debit</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 2,
                      }).format(selectedGlRow.debit || 0)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Credit</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 2,
                      }).format(selectedGlRow.credit || 0)}
                    </Typography>
                  </Box>
                </Box>
                {selectedGlRow.originalData?.pushDate && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Push date</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedGlRow.originalData.pushDate}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          <Divider sx={{ mb: 3 }} />

          {/* Comments Accordion */}
          <Accordion defaultExpanded sx={{ mb: 3, boxShadow: 'none', bgcolor: 'transparent' }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: ht.text }} />}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CommentIcon fontSize="small" sx={{ color: ht.text }} />
                <Typography variant="subtitle2" sx={harmonateDrawerAccordionTitleSx}>
                  Comments
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, ...harmonateFormFieldsSx }}>
                <TextField
                  multiline
                  rows={4}
                  placeholder="Add a comment..."
                  value={glComment}
                  onChange={(e) => setGlComment(e.target.value)}
                  variant="outlined"
                  size="small"
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    // TODO: Save comment to backend
                    console.log('Comment to save:', glComment);
                    // For now, just show a message
                    if (glComment.trim()) {
                      showSuccess('Comment added (UI only - backend integration pending)');
                    }
                  }}
                  disabled={!glComment.trim()}
                  sx={{ ...harmonateProceedButtonSx, alignSelf: 'flex-end' }}
                >
                  Add Comment
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Timing Bar */}
          <Box sx={{ mt: 'auto' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#000000' }}>
              Timing Bar
            </Typography>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: ht.listBg,
                borderRadius: ht.cardRadius,
                border: `1px solid ${ht.line}`,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Timing information will be displayed here
              </Typography>
              {/* TODO: Add timing bar component when backend is ready */}
            </Paper>
          </Box>
        </Box>
      </Drawer>

      {/* Add Adjustment Popup */}
      <AdjustmentPopup
        open={adjustmentDialogOpen}
        onClose={handleAdjustmentClose}
        onSubmit={handleAddAdjustment}
        selectedHotel={selectedProperty}
        hotelDetails={properties}
        selectedMonthYear={selectedBankMonthYear}
        editMode={editMode}
        editData={editData}
        showToast={showToast}
        showError={showError}
      />

      {/* Debug: Show glFilters.hotel value */}
      {console.log('Mapping.jsx - glFilters.hotel:', glFilters.hotel)}

      {/* Bank Drawer */}
      <BankDrawer
        open={bankDrawerOpen}
        onClose={() => setBankDrawerOpen(false)}
      />

      {/* Chart of Accounts Drawer */}
      <ChartOfAccountsDrawer
        open={chartOfAccountsDrawerOpen}
        onClose={() => setChartOfAccountsDrawerOpen(false)}
      />

      {/* GL Transaction Drawer */}
      <GLTransactionDrawer
        open={glTransactionDrawerOpen}
        onClose={() => setGlTransactionDrawerOpen(false)}
      />

      {/* Bank Statement Drawer */}
      <BankStatementDrawer
        open={bankStatementDrawerOpen}
        onClose={() => setBankStatementDrawerOpen(false)}
      />

      {/* Refresh Data Dialog */}
      <RefreshData
        open={refreshDataOpen}
        onClose={() => setRefreshDataOpen(false)}
        showSuccess={showSuccess}
        showError={showError}
        showToast={showToast}
      />

      {/* GL Upload Dialog */}
      <GLUpload
        open={glUploadDialogOpen}
        onClose={() => setGlUploadDialogOpen(false)}
        onUploadComplete={(results) => {
          console.log('GL Upload completed:', results);
        }}
      />

      {/* Bank Upload Dialog */}
      <BankUpload
        open={bankUploadDialogOpen}
        onClose={() => setBankUploadDialogOpen(false)}
        onUploadComplete={(results) => {
          console.log('Bank Upload completed:', results);
        }}
      />

      {/* Unified Upload Dialog */}
      <UnifiedUpload
        open={unifiedUploadDialogOpen}
        onClose={() => setUnifiedUploadDialogOpen(false)}
        onUploadComplete={(results) => {
          console.log('Unified Upload completed:', results);
        }}
      />

      {/* Toast Notification */}
      <Toast
        open={toastState.open}
        onClose={hideToast}
        message={toastState.message}
        severity={toastState.severity}
        title={toastState.title}
        autoHideDuration={toastState.autoHideDuration}
        position={toastState.position}
      />

      {/* Bulk Reclassify Confirmation Dialog */}
      <Dialog
        open={bulkReclassifyDialogOpen}
        onClose={() => setBulkReclassifyDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>Reclassify Transactions</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography>
            Are you sure you want to reclassify {selectedGlRows.length} transaction(s)? 
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={() => setBulkReclassifyDialogOpen(false)}
            variant="outlined"
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkReclassify}
            variant="contained"
            disabled={reclassifyingBulk}
            startIcon={reclassifyingBulk ? <CircularProgress size={16} /> : <DeleteIcon />}
            sx={harmonateProceedButtonSx}
          >
            {reclassifyingBulk ? 'Reclassifying...' : `Reclassify (${selectedGlRows.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Push to another date Dialog */}
      <Dialog
        open={pushToDateDialogOpen}
        onClose={() => {
          if (!pushing) {
            setPushToDateDialogOpen(false);
            setGlRecordForPush(null);
            setPushDate('');
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>Push to another date</DialogTitle>
        <Divider />
        <DialogContent sx={{ ...harmonateDialogContentSx, ...harmonateFormFieldsSx }}>
          {glRecordForPush && (
            <>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>Transaction details</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                <Typography variant="body2"><strong>Description:</strong> {glRecordForPush.description || '—'}</Typography>
                <Typography variant="body2"><strong>Date:</strong> {glRecordForPush.date || '—'}</Typography>
                <Typography variant="body2"><strong>Doc No:</strong> {glRecordForPush.docNo || '—'}</Typography>
                <Typography variant="body2"><strong>Debit:</strong> {glRecordForPush.debit != null ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(glRecordForPush.debit) : '—'}</Typography>
                <Typography variant="body2"><strong>Credit:</strong> {glRecordForPush.credit != null ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(glRecordForPush.credit) : '—'}</Typography>
                {glRecordForPush.originalData?.glAccountNo != null && (
                  <Typography variant="body2"><strong>GL Account No:</strong> {glRecordForPush.originalData.glAccountNo}</Typography>
                )}
                {glRecordForPush.originalData?.glAccountName != null && (
                  <Typography variant="body2"><strong>GL Account Name:</strong> {glRecordForPush.originalData.glAccountName}</Typography>
                )}
                {glRecordForPush.originalData?.transactionType != null && (
                  <Typography variant="body2"><strong>Transaction Type:</strong> {glRecordForPush.originalData.transactionType}</Typography>
                )}
              </Box>
              <TextField
                fullWidth
                size="small"
                label="Push date"
                type="date"
                value={pushDate}
                onChange={(e) => setPushDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mt: 1 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={() => {
              setPushToDateDialogOpen(false);
              setGlRecordForPush(null);
              setPushDate('');
            }}
            variant="outlined"
            disabled={pushing}
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePushToDate}
            variant="contained"
            disabled={pushing || !pushDate}
            startIcon={pushing ? <CircularProgress size={16} /> : null}
            sx={harmonateProceedButtonSx}
          >
            {pushing ? 'Pushing...' : 'Push'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Mapping;

